import { useState, useCallback, useEffect } from "react";
import { Sparkles, Scissors, Layers, Layers2, Briefcase, GlassWater, Gem, Heart, Wind, Eye, ChevronDown, Loader2, RotateCcw, Palette, Info, Lock, Unlock } from "lucide-react";
import { callImagenWithRetry, sleep, looksLikeSplitComposite } from "./imagenClient";
import { callAnthropicMessages } from "./anthropicClient";

const SILHOUETTES    = ['A-line','Ball Gown','Empire','Fit-and-Flare','Mermaid','Sheath','Slip','Trumpet','Column','Custom'];
const SKIRT_TYPES    = ['A-line','Gathered','Pencil','Pleated','Wrap','Custom'];
const GARMENT_LENGTHS = ['Mini','Above-knee','Knee','Midi','Tea','Ankle','Floor','Custom'];
const DRESS_LENGTHS  = GARMENT_LENGTHS;
const SKIRT_LENGTHS  = GARMENT_LENGTHS;
const SILHOUETTE_IMAGEN_DESC = {
  'A-line':        'A-line silhouette, fitted at the bodice and waist, skirt flaring gently to the hem like the letter A',
  'Ball Gown':     'ball gown silhouette, fitted structured bodice with dramatically full voluminous skirt',
  'Mermaid':       'mermaid silhouette, fitted through bodice hips and thighs, flaring outward below the knee',
  'Sheath':        'sheath silhouette, slim straight cut following the body from shoulder to hem with no flare',
  'Slip':          'slip dress silhouette, minimal bias-cut or straight cut with thin straps, following the body loosely',
  'Column':        'column silhouette, long straight narrow cut from shoulder to hem, minimal shaping',
  'Empire':        'empire waist silhouette, seam placed directly below the bust with skirt falling straight from the bust to the hem',
  'Fit-and-Flare': 'fit-and-flare silhouette, closely fitted through bodice and hips then flaring dramatically below the knee',
  'Trumpet':       'trumpet silhouette, closely fitted through bodice and thighs, skirt flaring outward below mid-thigh like a trumpet bell',
};

const FABRIC_IMAGEN_DESC = {
  'velvet':       'luxury silk-backed velvet with deep rich pile, sumptuous drape and deep light absorption, high-end couture fabric',
  'silk':         'pure silk with luminous sheen, fluid drape and natural lustre',
  'satin':        'high-lustre satin with mirror-like surface and elegant drape',
  'lace':         'fine French lace with intricate floral pattern and delicate structure',
  'chiffon':      'sheer flowing chiffon with soft lightweight drape',
  'crepe':        'matte crepe with smooth surface and sophisticated fluid drape',
  'georgette':    'semi-sheer georgette with subtle texture and graceful movement',
  'organza':      'crisp structured organza with slight transparency and body',
  'jacquard':     'woven jacquard with intricate raised pattern integral to the fabric',
  'brocade':      'richly woven brocade with raised metallic or coloured pattern',
  'linen':        'fine linen with natural texture and relaxed elegant drape',
  'wool':         'tailored wool suiting fabric with clean press and structured drape',
};

const GOAL_DEFAULT_DRESS_LENGTH = {
  business: 'Knee',
  cocktail: 'Midi',
  gala: 'Floor',
  bridal: 'Floor',
};
const DRESS_LENGTH_HEM_LOGIC = {
  'Mini': 'mini length, well above the knee',
  'Above-knee': 'above-knee length',
  'Knee': 'knee-length',
  'Midi': 'midi length, mid-calf',
  'Tea': 'tea length, lower-calf',
  'Ankle': 'ankle-length',
  'Floor': 'floor-length',
};

const DESIGN_DETAIL_OPTIONS = {
  bodice: ["Clean", "Structured bodice", "Bias-cut bodice", "Darted bodice", "Princess seams", "Corset-style seaming", "Wrapped bodice", "Draped bodice", "Ruched bodice", "Soft gathering"],
  waist: ["Clean waist", "Defined waist seam", "Dropped waist", "Basque waist", "Gathered waist", "Pleated waist", "Draped waist"],
  skirtBehavior: ["Clean fall", "Column fall", "Bias-cut skirt", "Gentle flare", "Soft gathers", "Knife pleats", "Box pleats", "Draped skirt", "Directional drape"],
  skirtFeature: ["None", "Front slit", "Side slit", "High slit", "Wrap opening", "Overskirt layer"],
  surface: ["None", "Beaded bodice", "Beaded detail (localized)", "Embroidered detail", "Lace appliqué", "Lace overlay", "Self-covered buttons (accent)", "Bow detail", "Contrast trim"],
  hem: ["None", "Sweep", "Court", "Chapel", "Cathedral", "Monarch"],
};

const DEFAULT_DESIGN_DETAILS = {
  bodice: "Clean",
  waist: "Clean waist",
  skirtBehavior: "Clean fall",
  skirtFeature: "None",
  surface: "None",
  hem: "Clean hem",
};

function buildDesignDetailsSummary(designDetails) {
  if (!designDetails) return "";
  const parts = [];
  if (designDetails.bodice && designDetails.bodice !== "Clean") {
    parts.push(designDetails.bodice);
  }
  if (designDetails.surface && designDetails.surface !== "None") {
    parts.push(`with ${designDetails.surface.toLowerCase()}`);
  }
  if (designDetails.waist && designDetails.waist !== "Clean waist") {
    parts.push(`above a ${designDetails.waist.toLowerCase()}`);
  }

  const skirtParts = [];
  if (designDetails.skirtBehavior && designDetails.skirtBehavior !== "Clean fall") {
    skirtParts.push(designDetails.skirtBehavior);
  }
  if (designDetails.skirtFeature && designDetails.skirtFeature !== "None") {
    skirtParts.push(designDetails.skirtFeature.toLowerCase());
  }
  if (skirtParts.length) {
    parts.push(`skirt with ${skirtParts.join(' and ').toLowerCase()}`);
  }

  if (designDetails.hem && designDetails.hem !== "Clean hem") {
    parts.push(designDetails.hem.toLowerCase());
  }

  if (!parts.length) return "Clean, unembellished design.";
  const sentence = parts.join(', ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
}

// Imagen-specific descriptions for skirt behavior design details
const SKIRT_BEHAVIOR_IMAGEN_DESC = {
  "Clean fall":       null,  // default, no override needed
  "Column fall":      "narrow straight column skirt with no flare — skirt falls in a slim straight line from waist to hem, no A-line shape, no volume",
  "Bias-cut skirt":   "bias-cut skirt, fabric cut on the diagonal, flowing and clinging gently to the body",
  "Gentle flare":     "skirt with gentle A-line flare, softly widening from hip to hem",
  "Soft gathers":     "softly gathered skirt with relaxed fullness at the waist",
  "Knife pleats":     "knife-pleated skirt, sharp uniform pleats falling from the waistband",
  "Box pleats":       "box-pleated skirt, structured pleats creating controlled volume",
  "Draped skirt":     "softly draped skirt with flowing fabric folds",
  "Directional drape":"directionally draped skirt with asymmetric fabric flow",
};

const WAIST_IMAGEN_DESC = {
  "Clean waist":      null,
  "Defined waist seam": "with a clean defined waist seam at the natural waist",
  "Dropped waist":    "with a dropped waistline — the waist seam sits below the natural waist at the hip",
  "Basque waist":     "with a basque waistband sitting below the natural waist",
  "Gathered waist":   "with soft gathers at the waistline",
  "Pleated waist":    "with a pleated waistline",
  "Draped waist":     "with soft fabric draped at the waist",
};

const BODICE_IMAGEN_DESC = {
  "Clean":              null,
  "Structured bodice":  "structured bodice with internal support, holding its shape",
  "Bias-cut bodice":    "bias-cut bodice, fabric cut on the diagonal for a soft clinging fit",
  "Darted bodice":      "dart-fitted bodice with visible shaping darts",
  "Princess seams":     "princess-seamed bodice with vertical seaming from shoulder to waist",
  "Corset-style seaming": "bodice with corset-style boning seams and structured construction",
  "Wrapped bodice":     "wrapped bodice, fabric crossing over the front",
  "Draped bodice":      "softly draped bodice with flowing fabric folds across the front",
  "Ruched bodice":      "ruched bodice with gathered fabric creating texture",
  "Soft gathering":     "bodice with soft gathers",
};

// Build a composite garment description that integrates structural design details
function buildCompositeGarmentDesc(spec) {
  const silhouetteDesc = SILHOUETTE_IMAGEN_DESC[spec.cut] || spec.cut || "A-line";
  const fabricDesc     = FABRIC_IMAGEN_DESC[spec.fabricId] || spec.fabricName || "fabric";
  const d              = spec.designDetails || {};

  // Structural overrides — these modify how the named silhouette is rendered
  const skirtOverride  = d.skirtBehavior && d.skirtBehavior !== "Clean fall"
    ? SKIRT_BEHAVIOR_IMAGEN_DESC[d.skirtBehavior] : null;
  const waistOverride  = d.waist && d.waist !== "Clean waist"
    ? WAIST_IMAGEN_DESC[d.waist] : null;
  const bodiceOverride = d.bodice && d.bodice !== "Clean"
    ? BODICE_IMAGEN_DESC[d.bodice] : null;
  const skirtFeature   = d.skirtFeature && d.skirtFeature !== "None"
    ? d.skirtFeature.toLowerCase() : null;

  // When skirt behavior overrides the silhouette, lead with that explicitly
  let shapeDesc;
  if (skirtOverride) {
    // The named silhouette still sets bodice fit, but skirt behavior takes precedence
    const bodiceContext = spec.cut === "A-line" || spec.cut === "Ball Gown"
      ? "fitted bodice"
      : spec.cut === "Mermaid" || spec.cut === "Trumpet"
        ? "closely fitted bodice and hips"
        : "fitted bodice";
    shapeDesc = `${bodiceContext} with ${skirtOverride}`;
  } else {
    shapeDesc = silhouetteDesc;
  }

  const waistDesc  = waistOverride ? `, ${waistOverride}` : "";
  const bodiceDesc = bodiceOverride ? `, ${bodiceOverride}` : "";
  const featureDesc = skirtFeature ? ` with ${skirtFeature}` : "";

  if (spec.separates) {
    const skirtLenDesc = DRESS_LENGTH_HEM_LOGIC[spec.skirtLength] || `${String(spec.skirtLength || "midi").toLowerCase()} length`;
    return `${fabricDesc} tailored jacket with ${spec.jacketNeckline} neckline and ${spec.jacketSleeves} sleeves, paired with a ${spec.skirtType} skirt, ${skirtLenDesc}`;
  }

  const dressLenDesc = DRESS_LENGTH_HEM_LOGIC[spec.dressLength] || `${String(spec.dressLength || "midi").toLowerCase()} length`;
  return `${fabricDesc} dress${bodiceDesc}${waistDesc}, ${shapeDesc}${featureDesc}, with ${spec.neckline} front neckline, ${spec.backNeckline} back neckline, and ${spec.sleeves} sleeves at ${dressLenDesc}`;
}

const FABRIC_GROUPS = [
  { group: 'Standard', fabrics: [
    { id:'brocade',   name:'Brocade',   drape:'structured',           editorial:"The fabric of ceremony. Brocade carries history in its weave — intricate patterns that catch candlelight and command attention. Best reserved for structured designs where the fabric is the statement." },
    { id:'chiffon',   name:'Chiffon',   drape:'fluid',                editorial:"Movement made visible. Chiffon doesn't just drape — it responds to air, to gesture, to the subtle architecture of a body in motion. The choice for designs meant to feel effortless." },
    { id:'crepe',     name:'Crepe',     drape:'semi-fluid',           editorial:"The sophisticated choice for clients who know that restraint is its own luxury. Crepe whispers where satin shouts — and on the right silhouette, it's unforgettable." },
    { id:'georgette', name:'Georgette', drape:'fluid',                editorial:"Chiffon's more grounded sister — equally beautiful in motion but with enough substance to hold a seam and honor a silhouette. The workhorse of elegant eveningwear." },
    { id:'jacquard',  name:'Jacquard',  drape:'structured',           editorial:"Art that you wear. Jacquard carries its pattern in the weave itself — not printed, not applied, but intrinsic. A garment in jacquard doesn't just look extraordinary; it is extraordinary at the level of its construction." },
    { id:'lace',      name:'Lace',      drape:'semi-structured',      editorial:"The language of romance in textile form. Lace is architecture and shadow simultaneously — structure you can see through, pattern that casts its own light. No other fabric carries the same emotional weight." },
    { id:'linen',     name:'Linen',     drape:'semi-structured',      editorial:"Honest and unhurried. Linen ages into beauty — it carries the history of the wearer in its softening fibers. For designs that should feel effortless and endure." },
    { id:'organza',   name:'Organza',   drape:'structured-sheer',     editorial:"Volume with transparency. Organza is the sculptor's medium in fashion — it holds the shapes imagination creates. Used for sleeves that seem to float, skirts that stand away from the body, overlays that catch every light in the room." },
    { id:'satin',     name:'Satin',     drape:'semi-fluid',           editorial:"The fabric of transformation. Satin turns light into something a garment wears — pooling in the shadows, blazing at the highlights. On the right silhouette, it is simply incomparable." },
    { id:'silk',      name:'Silk',      drape:'fluid',                editorial:"The original luxury. Silk has been the fabric of courts, ceremonies, and intimate moments for five thousand years — there is no substitute for its warmth against skin or the way it catches light with something resembling intelligence." },

    { id:'velvet',    name:'Velvet',    drape:'fluid-heavy',          editorial:"The fabric of deep winter evenings and candlelit ceremonies. Velvet doesn't just absorb light — it hoards it, releasing depth and color that no other textile can replicate. To touch velvet is to understand why luxury exists." },
    { id:'wool',      name:'Wool',      drape:'structured',           editorial:"The backbone of craft. Wool has been the fabric of makers for centuries — it responds to the tailor's hand, remembers its shaping, and ages into something more itself. For designs built to last." },
  ]},
  { group: 'Sustainable', fabrics: [
    { id:'deadstock-silk',  name:'Deadstock Silk',     drape:'fluid',           sustainable:true, editorial:"Provenance in every yard. Deadstock silk carries the story of the collections it almost became — it exists because somewhere, a designer overestimated or a season changed. To use it is to complete that interrupted arc." },
    { id:'organic-cotton',  name:'Organic Cotton',     drape:'semi-structured', sustainable:true, editorial:"Simplicity as a philosophy. Organic cotton begins in cleaner soil — fewer chemicals, quieter farming. Still thirsty by nature, but honest about it. For designs whose integrity runs through to the fiber." },
    { id:'tencel',          name:'Tencel / Lyocell',   drape:'fluid',           sustainable:true, editorial:"Tencel drapes like silk, breathes like linen, washes like cotton — and its closed-loop process recaptures most of what it uses. Not a zero-impact fabric, but a thoughtful one. For the designer who reads the small print." },
  ]},
  { group: 'Custom', fabrics: [
    { id:'custom', name:'Custom', drape:null, editorial:null },
  ]},
];
// Flat list for lookups
const FABRIC_LIBRARY = FABRIC_GROUPS.flatMap(g => g.fabrics);
const SLEEVE_LIBRARY   = ["Bell Sleeve","Bishop Sleeve","Bracelet Sleeve","Butterfly Sleeve","Cap Sleeve","Elbow-Length","Flutter Sleeve","Juliet Sleeve","Lantern Sleeve","Long Sleeve","Puff Sleeve","Short Sleeve","Three-Quarter","Sleeveless","Custom"];
const JACKET_SLEEVE_LIBRARY = ["Long Sleeve","Three-Quarter","Elbow-Length","Short Sleeve","Cap Sleeve","Sleeveless","Custom"];
const NECKLINE_LIBRARY = ["V-Neck","Boat Neck","Cowl Neck","Halter Neck","Illusion Neckline","Keyhole Neckline","Off-the-Shoulder","One-Shoulder","Round Neck","Square Neck","Sweetheart","Custom"];
const BACK_NECKLINE_LIBRARY = ["Bow Back","Closed Back","Cowl Back","Deep V-Back","Illusion Back","Keyhole Back","Low V-Back","Open Back","Scoop Back","Square Back","Straight Back","Custom"];
const CLOSURE_LIBRARY = ["Concealed Snap Placket","Corset Lacing — Centre Back","Hook & Eye — Centre Back","Invisible Zip — Centre Back","Invisible Zip — Side Seam","Pull-On (No Closure)","Self-Covered Buttons — Centre Back","Custom"];
const JACKET_NECKLINE_LIBRARY = ["Notch Lapel","Peak Lapel","Shawl Collar","Collarless","Mandarin Collar","V-Neck","Round Neck","Custom"];

const DESIGN_GOALS = [
  { id:'business', name:'Business', Icon:Briefcase, recommendedFabricId:'wool',
    defaultAtmosphere:"A high-ceiling boardroom in a glass skyscraper overlooking a foggy morning metropolis.",
    hemLogic:"professional knee-length or midi-length" },
  { id:'cocktail', name:'Cocktail', Icon:GlassWater, recommendedFabricId:'silk',
    defaultAtmosphere:"An upscale rooftop lounge at golden hour with soft amber city lights.",
    hemLogic:"elegant cocktail length, hitting just above or at the knee" },
  { id:'gala', name:'Gala / Black Tie', Icon:Gem, recommendedFabricId:'velvet',
    defaultAtmosphere:"A grand marble ballroom with crystal chandeliers and live orchestral music.",
    hemLogic:"floor-length with a dramatic sweep" },
  { id:'bridal', name:'Bridal', Icon:Heart, recommendedFabricId:'satin',
    defaultAtmosphere:"A historic stone cathedral or a manicured garden estate.",
    hemLogic:"full floor-length with a traditional bridal train" },
];

const GOAL_ATMOSPHERE = DESIGN_GOALS.reduce(function(acc, goal) {
  acc[goal.id] = goal.defaultAtmosphere;
  return acc;
}, {});

const CURATED_SETTINGS = [
  { id:'beach',       label:'Beachside at sunset',    description:'An elevated coastal stone terrace overlooking the Mediterranean at golden hour, warm natural light, sea visible.' },
  { id:'cathedral',   label:'Cathedral or chapel',    description:'The interior of a soaring Gothic stone cathedral with stained glass windows and candlelit side aisles.' },
  { id:'country',     label:'Country house',          description:'The grounds of an English country house in summer, manicured lawns, weathered stone facade, long afternoon light.' },
  { id:'penthouse',   label:'Executive penthouse',    description:'A minimalist glass-walled penthouse interior with floor-to-ceiling windows overlooking a city skyline, clean architectural lines.' },
  { id:'garden',      label:'Garden estate',          description:'A manicured formal garden of a historic English country estate, hedgerows, gravel path, soft afternoon light.' },
  { id:'ballroom',    label:'Grand ballroom',         description:'A grand European ballroom with ornate gilded walls, parquet floors, warm candlelight and a live orchestra in the background.' },
  { id:'museum',      label:'Museum or gallery',      description:'A grand neoclassical museum interior with marble floors, tall columns and natural light from high windows.' },
  { id:'rooftop',     label:'Rooftop at golden hour', description:'A private rooftop terrace high above a city at dusk, warm amber light, city skyline visible below.' },
  { id:'sacred-architecture', label:'Sacred architecture', description:'A grand ceremonial interior with high arches, refined geometric detailing, and soft filtered light. Serene, expansive, and quietly luminous.' },
];

const DESIGN_TAGS = [
  "Romantic & soft",
  "Powerful & commanding",
  "Architectural & precise",
  "Effortless & fluid",
  "Dramatic & memorable",
  "Understated & elegant",
  "Joyful & celebratory",
  "Quietly confident",
  "Bold & unapologetic",
  "Timeless over fashionable",
  "Vintage & storied",
  "Sculptural & avant-garde",
];

const COLOR_MAP = {
  'dusty rose':'#c9a0a0','rose':'#e8b4b8','blush':'#f4c2c2','blush pink':'#ffb6c1',
  'ivory':'#fffff0','cream':'#fffdd0','champagne':'#f7e7ce','ecru':'#c2b280',
  'navy':'#000080','midnight navy':'#1e293b','deep navy':'#0a1628','cobalt':'#0047ab',
  'sage':'#b2ac88','sage green':'#9cac8b','olive':'#808000','forest green':'#228b22',
  'burgundy':'#800020','wine':'#722f37','merlot':'#731843','cranberry':'#9c143c',
  'black':'#1a1a1a','jet black':'#0a0a0a','charcoal':'#36454f','slate':'#708090',
  'white':'#fefefe','snow white':'#fffafa','pearl':'#f0ece3',
  'lavender':'#e6e6fa','lilac':'#c8a2c8','mauve':'#e0b0ff','plum':'#8e4585',
  'gold':'#d4af37','champagne gold':'#c5a028','bronze':'#cd7f32','copper':'#b87333',
  'silver':'#c0c0c0','platinum':'#e5e4e2',
  'terracotta':'#c66a4a','rust':'#b7410e','burnt orange':'#cc5500','coral':'#ff7f50',
  'teal':'#008080','emerald':'#50c878','jade':'#00a86b','mint':'#98ff98',
  'sky blue':'#87ceeb','powder blue':'#b0e0e6','cerulean':'#007ba7','periwinkle':'#ccccff',
  'camel':'#c19a6b','tan':'#d2b48c','taupe':'#483c32','nude':'#e3bc9a',
};
const COLOR_ALIAS_MAP = {
  'blue':'#1e40af',
  'royal blue':'#1d4ed8',
  'sapphire':'#0f52ba',
  'grey':'#708090',
  'gray':'#708090',
  'pink':'#ffb6c1',
  'purple':'#7e22ce',
  'magenta':'#d946ef',
  'fuchsia':'#ff00ff',
  'red':'#b91c1c',
  'green':'#228b22',
  'orange':'#ea580c',
  'yellow':'#eab308',
  'brown':'#8b5a2b',
};
const HEX_COLOR_RE = /#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/;
const COLOR_LOOKUP_ENTRIES = [...Object.entries(COLOR_MAP), ...Object.entries(COLOR_ALIAS_MAP)]
  .sort(function(a, b) { return b[0].length - a[0].length; });

// Hollow-to-hem ratio ranges by garment length.
// Formula: hollowToHem / height should fall within this range.
export const LENGTH_RATIOS = {
  Mini: { min: 0.30, max: 0.45, description: "mid-thigh length", visualGuide: "Several inches above knee" },
  "Above-knee": { min: 0.43, max: 0.55, description: "above-knee length", visualGuide: "2-4 inches above kneecap" },
  Knee: { min: 0.53, max: 0.65, description: "knee-length", visualGuide: "Hem grazes kneecap" },
  Midi: { min: 0.63, max: 0.76, description: "mid-calf length", visualGuide: "Halfway between knee and ankle" },
  Tea: { min: 0.74, max: 0.85, description: "tea-length (lower calf)", visualGuide: "Between mid-calf and ankle" },
  Ankle: { min: 0.83, max: 0.94, description: "ankle-length", visualGuide: "Grazes ankle bone, above foot" },
  Floor: { min: 0.92, max: 1.05, description: "floor-length", visualGuide: "Touches floor, may pool slightly" },
};

function normalizeLegacyLength(value) {
  return value === "Maxi" ? "Floor" : value;
}

function normalizeHex(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const prefixed = raw.startsWith("#") ? raw : ("#" + raw);
  if (!/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(prefixed)) return null;
  if (prefixed.length === 4) {
    return ("#" + prefixed[1] + prefixed[1] + prefixed[2] + prefixed[2] + prefixed[3] + prefixed[3]).toLowerCase();
  }
  return prefixed.toLowerCase();
}

function normalizeColorDescription(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9#\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function resolveColorHex(value) {
  const normalized = normalizeColorDescription(value);
  if (!normalized) return null;
  const explicitHex = normalized.match(HEX_COLOR_RE);
  if (explicitHex) return normalizeHex(explicitHex[0]);
  if (COLOR_MAP[normalized]) return COLOR_MAP[normalized];
  const match = COLOR_LOOKUP_ENTRIES.find(function(entry) {
    return normalized.includes(entry[0]);
  });
  return match ? match[1] : null;
}

const C = '#2D2926';

// Imagen requests are routed through /api/imagen proxy.

const COLOUR_PALETTE = [
  { group: 'Pink, Rose & Violet', colours: [
    { name:'Petal',          hex:'#f9e0e0', imagenDesc:'pale petal pink, very soft blush' },
    { name:'Blush',          hex:'#f4c2c2', imagenDesc:'soft blush pink, delicate pale rose' },
    { name:'Rose',           hex:'#e8b4b8', imagenDesc:'soft rose pink, warm petal tone' },
    { name:'Dusty Rose',     hex:'#c9a0a0', imagenDesc:'dusty rose, muted antique pink' },
    { name:'Antique Rose',   hex:'#cd9898', imagenDesc:'antique rose — a medium-depth muted pink with warm terracotta undertone, darker and more earthy than blush, lighter and warmer than mauve, not dusty, not bright, not beige' },
    { name:'Deep Rose',      hex:'#d4537e', imagenDesc:'deep rose, rich warm pink' },
    { name:'Hibiscus',       hex:'#b5485d', imagenDesc:'hibiscus, deep warm rose pink' },
    { name:'Mauve',          hex:'#8e6b7a', imagenDesc:'mauve, dusty muted purple-rose' },
    { name:'Lilac',          hex:'#c8a2c8', imagenDesc:'soft lilac, pale purple' },
    { name:'Wisteria',       hex:'#9b8faf', imagenDesc:'wisteria, soft grey-purple' },
    { name:'Amethyst',       hex:'#7b5ea7', imagenDesc:'amethyst, rich medium purple' },
    { name:'Plum',           hex:'#5c2d4e', imagenDesc:'deep plum, dark rich purple' },
  ]},
  { group: 'Red, Wine & Bordeaux', colours: [
    { name:'Blush Coral',    hex:'#e8998d', imagenDesc:'blush coral, warm soft salmon' },
    { name:'Coral',          hex:'#e2725b', imagenDesc:'coral, warm orange-red' },
    { name:'Terracotta',     hex:'#c66a4a', imagenDesc:'terracotta, warm earthy clay tone' },
    { name:'Brick',          hex:'#9e3d2b', imagenDesc:'brick red, deep warm red-brown' },
    { name:'Scarlet',        hex:'#c0392b', imagenDesc:'scarlet, vivid warm red' },
    { name:'Crimson',        hex:'#a31515', imagenDesc:'deep rich red, vivid saturated red tone' },
    { name:'Bordeaux',       hex:'#6d1a36', imagenDesc:'bordeaux, deep dark wine, rich dark rose-red' },
    { name:'Burgundy',       hex:'#800020', imagenDesc:'deep wine, dark rose-red, bordeaux' },
    { name:'Wine',           hex:'#722f37', imagenDesc:'dark wine rose, deep merlot' },
    { name:'Garnet',         hex:'#5c2323', imagenDesc:'garnet, very deep dark red' },
    { name:'Oxblood',        hex:'#4a1010', imagenDesc:'oxblood, extremely deep dark red-brown' },
    { name:'Auburn',         hex:'#922b21', imagenDesc:'auburn, warm deep red-brown' },
  ]},
  { group: 'Warm, Earth & Spice', colours: [
    { name:'Peach',          hex:'#f5c6a0', imagenDesc:'peach, soft warm orange-pink' },
    { name:'Apricot',        hex:'#eea882', imagenDesc:'apricot, warm soft orange' },
    { name:'Burnt Orange',   hex:'#cc5500', imagenDesc:'warm burnt orange, deep amber-orange' },
    { name:'Amber',          hex:'#c87941', imagenDesc:'amber, warm golden orange-brown' },
    { name:'Caramel',        hex:'#c68642', imagenDesc:'caramel, warm golden brown' },
    { name:'Camel',          hex:'#c19a6b', imagenDesc:'camel, warm tan, soft light brown' },
    { name:'Warm Sand',      hex:'#e3bc9a', imagenDesc:'warm sand, light beige, soft natural tone' },
    { name:'Wheat',          hex:'#d4b896', imagenDesc:'wheat, pale warm beige' },
    { name:'Mushroom',       hex:'#b5a49a', imagenDesc:'mushroom, warm grey-beige' },
    { name:'Taupe',          hex:'#8b7d72', imagenDesc:'taupe, warm grey-brown' },
    { name:'Mocha',          hex:'#6f4e37', imagenDesc:'mocha, warm dark brown' },
    { name:'Chocolate',      hex:'#4a2c1a', imagenDesc:'chocolate, very deep dark brown' },
  ]},
  { group: 'White, Ivory & Cream', colours: [
    { name:'Snow White',     hex:'#fefefe', imagenDesc:'pure white, crisp bright white' },
    { name:'Ivory',          hex:'#fffff0', imagenDesc:'ivory, warm off-white, cream white' },
    { name:'Cream',          hex:'#fff8e7', imagenDesc:'cream, soft warm white' },
    { name:'Champagne',      hex:'#f7e7ce', imagenDesc:'champagne, warm pale gold, soft ecru' },
    { name:'Ecru',           hex:'#c2b280', imagenDesc:'ecru, natural linen tone, warm off-white' },
    { name:'Pearl',          hex:'#f0ece3', imagenDesc:'pearl, soft lustrous off-white' },
    { name:'Dove',           hex:'#d4cfc9', imagenDesc:'dove grey, very soft warm grey' },
    { name:'Silver Grey',    hex:'#a8a9ad', imagenDesc:'silver grey, cool medium grey' },
    { name:'Slate',          hex:'#708090', imagenDesc:'slate grey, medium cool grey' },
  ]},
  { group: 'Black, Charcoal & Midnight', colours: [
    { name:'Black',          hex:'#1a1a1a', imagenDesc:'classic black, deep jet black' },
    { name:'Onyx',           hex:'#2d2d2d', imagenDesc:'onyx, near black, very deep grey' },
    { name:'Charcoal',       hex:'#4a4a4a', imagenDesc:'charcoal, very dark grey' },
    { name:'Graphite',       hex:'#5a5a5a', imagenDesc:'graphite, dark medium grey' },
    { name:'Midnight Black', hex:'#0d0d0d', imagenDesc:'midnight black, pure deep black' },
    { name:'Ink Black',      hex:'#1a1a2e', imagenDesc:'ink black, very deep blue-black' },
  ]},
  { group: 'Blue, Navy & Indigo', colours: [
    { name:'Ice Blue',       hex:'#daeef7', imagenDesc:'ice blue, very pale cool blue' },
    { name:'Powder Blue',    hex:'#b0e0e6', imagenDesc:'powder blue, soft pale sky blue' },
    { name:'Sky Blue',       hex:'#87ceeb', imagenDesc:'sky blue, light clear blue' },
    { name:'Periwinkle',     hex:'#ccccff', imagenDesc:'periwinkle, soft blue-violet' },
    { name:'Cornflower',     hex:'#6495ed', imagenDesc:'cornflower blue, medium bright blue' },
    { name:'Cobalt',         hex:'#0047ab', imagenDesc:'cobalt blue, bold vivid blue' },
    { name:'Royal Blue',     hex:'#1d4ed8', imagenDesc:'royal blue, rich deep vivid blue' },
    { name:'Sapphire',       hex:'#0f52ba', imagenDesc:'sapphire blue, deep jewel blue' },
    { name:'Midnight Blue',  hex:'#191970', imagenDesc:'midnight blue, very deep dark blue' },
    { name:'Midnight Navy',  hex:'#1e293b', imagenDesc:'midnight navy, very deep dark blue-black' },
    { name:'Ink',            hex:'#1a1f3a', imagenDesc:'ink blue, near black deep blue' },
    { name:'Denim',          hex:'#6b8cae', imagenDesc:'denim blue, medium casual blue' },
  ]},
  { group: 'Green, Sage & Teal', colours: [
    { name:'Mint',           hex:'#d4edda', imagenDesc:'mint, very pale soft green' },
    { name:'Sage',           hex:'#9cac8b', imagenDesc:'sage green, soft muted olive green' },
    { name:'Pistachio',      hex:'#93c572', imagenDesc:'pistachio, light fresh green' },
    { name:'Moss',           hex:'#8a9a5b', imagenDesc:'moss green, muted earthy green' },
    { name:'Olive',          hex:'#6b7c3a', imagenDesc:'olive green, warm dark yellow-green' },
    { name:'Hunter Green',   hex:'#355e3b', imagenDesc:'hunter green, deep rich green' },
    { name:'Forest Green',   hex:'#228b22', imagenDesc:'forest green, deep rich green' },
    { name:'Emerald',        hex:'#50c878', imagenDesc:'emerald green, rich jewel green' },
    { name:'Jade',           hex:'#00a86b', imagenDesc:'jade green, deep cool green' },
    { name:'Teal',           hex:'#008080', imagenDesc:'teal, deep blue-green' },
    { name:'Peacock',        hex:'#006064', imagenDesc:'peacock, very deep blue-green' },
    { name:'Spruce',         hex:'#1f4037', imagenDesc:'spruce, very deep dark green' },
  ]},
  { group: 'Gold, Silver & Metallic', colours: [
    { name:'Champagne Gold', hex:'#f0d080', imagenDesc:'champagne gold, pale warm gold' },
    { name:'Gold',           hex:'#d4af37', imagenDesc:'gold, warm rich metallic gold' },
    { name:'Antique Gold',   hex:'#b8970a', imagenDesc:'antique gold, deep warm aged gold' },
    { name:'Bronze',         hex:'#cd7f32', imagenDesc:'bronze, warm metallic copper-gold' },
    { name:'Copper',         hex:'#b87333', imagenDesc:'copper, warm metallic bronze' },
    { name:'Rose Gold',      hex:'#b76e79', imagenDesc:'rose gold, warm metallic pink-gold' },
    { name:'Silver',         hex:'#c0c0c0', imagenDesc:'silver, cool metallic silver' },
    { name:'Platinum',       hex:'#e5e4e2', imagenDesc:'platinum, very pale cool metallic' },
    { name:'Pewter',         hex:'#8f8f8f', imagenDesc:'pewter, medium warm grey metallic' },
    { name:'Gunmetal',       hex:'#4a4e52', imagenDesc:'dark metallic grey, deep cool steel' },
    { name:'Burnished Gold', hex:'#9a7d2a', imagenDesc:'burnished gold, deep antique warm gold' },
    { name:'Mercury',        hex:'#b5b5b5', imagenDesc:'mercury silver, bright cool metallic' },
  ]},
];

const INITIAL_FORM = {
  category:DESIGN_GOALS[2].id, colorDescription:"Dusty Rose", colorHex:"#c9a0a0",
  fabricId:"velvet", customFabric:"", lining:"Fully Lined",
  // dress fields
  cut:'A-line', customCut:"", neckline:"V-Neck", customNeckline:"", backNeckline:"Low V-Back", customBackNeckline:"",
  sleeves:"Cap Sleeve", customSleeves:"", closure:"Invisible Zip — Side Seam", customClosure:"",
  dressLength:'Floor', customDressLength:'',
  // separates fields
  separates: false,
  skirtType:'A-line', customSkirtType:'',
  skirtLength:'Knee', customSkirtLength:'',
  jacketNeckline:"Notch Lapel", customJacketNeckline:'',
  jacketSleeves:"Long Sleeve", customJacketSleeves:'',
  jacketFront:"",
  jacketBack:"",
  skirtFront:"",
  skirtBack:"",
  // design details
  designDetails: { ...DEFAULT_DESIGN_DETAILS },
  designDetailsSummary: buildDesignDetailsSummary(DEFAULT_DESIGN_DETAILS),
  originalComments: '',
  aiInterpretedComments: '',
  // shared
  atmosphere:'A grand marble ballroom with crystal chandeliers and live orchestral music.',
  frontDescription:'',
  backDescription:'',
};

const S = {
  page:    { margin:0, padding:0, minHeight:'100vh', backgroundColor:'#F7F4F1', color:'#2D2926', fontFamily:'system-ui,sans-serif', fontSize:15, lineHeight:1.45 },
  header:  { margin:0, padding:'18px 28px', backgroundColor:'#C9A0A0', borderBottom:'1px solid #b88f8f', display:'flex', alignItems:'center', position:'sticky', top:0, zIndex:50 },
  main:    { margin:'0 auto', padding:'28px 20px 64px', maxWidth:700, display:'flex', flexDirection:'column', gap:20 },
  card:    { margin:0, padding:24, backgroundColor:'#ffffff', borderRadius:24, border:'1px solid #e9dde4', boxShadow:'0 10px 30px rgba(0,0,0,0.24)' },
  secHead: { margin:0, padding:0, paddingBottom:12, marginBottom:18, display:'flex', alignItems:'center', gap:8, borderBottom:'1px solid #f3ecef', fontSize:11, fontWeight:700, letterSpacing:'0.14em', color:'#5f5852', textTransform:'uppercase' },
  lbl:     { display:'block', margin:0, padding:0, marginBottom:6, fontSize:11, fontWeight:700, letterSpacing:'0.13em', color:'#5f5852', textTransform:'uppercase' },
  inp:     { display:'block', margin:0, width:'100%', padding:'10px 14px', backgroundColor:'#fff', borderRadius:10, border:'1px solid #e6dce2', fontSize:11, fontWeight:600, outline:'none', fontFamily:'system-ui,sans-serif', color:'#57534e' },
  sel:     { display:'block', margin:0, width:'100%', padding:'10px 36px 10px 14px', backgroundColor:'#fff', borderRadius:10, border:'1px solid #e6dce2', fontSize:11, fontWeight:600, outline:'none', fontFamily:'system-ui,sans-serif', color:'#57534e', cursor:'pointer', appearance:'none' },
  divider: { margin:'20px 0', border:'none', borderTop:'1px dashed #e8dce4' },
  ph:      { fontStyle:'italic', color:'#8f868d' },
  itx:     { fontStyle:'italic', color:'#7b7279', fontWeight:400 },
  subHead: { margin:0, padding:'8px 12px', marginBottom:14, backgroundColor:'#f5f5f4', borderRadius:8, fontSize:11, fontWeight:700, letterSpacing:'0.12em', color:'#5f5852', textTransform:'uppercase' },
};

// ── SelectField — defined outside App so it never remounts on re-render ─────
function SelectField({ field, lib, customField, ph, label, form, set, focusedField, setFocusedField }) {
  return (
    <div style={{margin:0, padding:0}}>
      <label style={S.lbl}>{label}</label>
      <div style={{position:'relative'}}>
        <select value={form[field]} onChange={set(field)} style={S.sel}>
          {lib.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
      </div>
      {form[field]==="Custom" && (
        <textarea
          style={{...S.inp, marginTop:6, minHeight:72, resize:'vertical',
            fontStyle: focusedField === customField ? 'normal' : 'italic',
            color: focusedField === customField ? '#1c1917' : '#a8a29e',
            fontWeight: focusedField === customField ? 400 : 300,
            transition:'font-style 0.1s, color 0.1s',
          }}
          placeholder={ph}
          value={form[customField]}
          onChange={set(customField)}
          onFocus={() => setFocusedField(customField)}
          onBlur={() => setFocusedField(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabitha welcome overlay
// ─────────────────────────────────────────────────────────────────────────────

// The original dress — Gala, Velvet, Dusty Rose, A-line, Floor, Boat Neck, Cap Sleeve
const TABITHA_DRESS_PRESET = {
  category: 'gala',
  colorDescription: 'Dusty Rose',
  colorHex: '#c9a0a0',
  fabricId: 'velvet',
  customFabric: '',
  lining: 'Fully Lined',
  cut: 'A-line',
  customCut: '',
  neckline: 'V-Neck',
  customNeckline: '',
  backNeckline: 'Low V-Back',
  customBackNeckline: '',
  sleeves: 'Cap Sleeve',
  customSleeves: '',
  closure: 'Invisible Zip — Side Seam',
  customClosure: '',
  dressLength: 'Floor',
  customDressLength: '',
  separates: false,
  skirtType: 'A-line',
  customSkirtType: '',
  skirtLength: 'Floor',
  customSkirtLength: '',
  jacketNeckline: 'V-Neck',
  customJacketNeckline: '',
  jacketSleeves: 'Cap Sleeve',
  customJacketSleeves: '',
  jacketFront: '',
  jacketBack: '',
  skirtFront: '',
  skirtBack: '',
  atmosphere: 'A grand marble ballroom with crystal chandeliers and live orchestral music.',
  frontDescription: '',
  backDescription: '',

  designDetails: { ...DEFAULT_DESIGN_DETAILS },
  designDetailsSummary: buildDesignDetailsSummary(DEFAULT_DESIGN_DETAILS),
  originalComments: '',
  aiInterpretedComments: '',
};

const TABITHA_DESIGN_NO = '862196';

function TabithaWelcome({ onBegin }) {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:1000,
      backgroundColor:'#2A2827',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      padding:'28px 24px',
      overflowY:'auto',
      animation:'fadeIn 0.8s ease',
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
      `}</style>

      {/* Brand name + studio */}
      <div style={{ textAlign:'center', marginBottom:20, marginTop:48 }}>
        <div style={{
          fontSize:18, fontWeight:700, letterSpacing:'0.55em',
          color:'#C9A0A0', textTransform:'uppercase',
          fontFamily:'system-ui,sans-serif', marginBottom:6,
        }}>
          Tabitha
        </div>
        <div style={{
          fontSize:11, fontWeight:500, letterSpacing:'0.35em',
          color:'rgba(255,255,255,0.35)', textTransform:'uppercase',
          fontFamily:'system-ui,sans-serif',
        }}>
          Studio
        </div>
      </div>

      {/* Dress image */}
      <div style={{
        position:'relative',
        borderRadius:20,
        overflow:'hidden',
        boxShadow:'0 30px 80px rgba(0,0,0,0.55)',
        width:'100%',
        maxWidth:360,
        aspectRatio:'9:16',
        marginBottom:22,
        backgroundColor:'#1a1714',
        flexShrink:0,
      }}>
        <img
          src="/Tabitha-Design-862196.jpg"
          alt="A gown designed in Tabitha Studio"
          style={{
            display:'block',
            width:'100%',
            height:'100%',
            objectFit:'cover',
            objectPosition:'center top',
          }}
        />
        {/* Design number — bottom-right */}
        <div style={{
          position:'absolute', bottom:12, right:14,
          fontSize:9, fontWeight:400, letterSpacing:'0.12em',
          color:'rgba(255,255,255,0.65)',
          fontFamily:'system-ui,sans-serif',
          textTransform:'uppercase',
          pointerEvents:'none',
        }}>
          Tabitha — Design No. 413376
        </div>

      </div>

      {/* Tagline */}
      <div style={{
        fontSize:12, color:'rgba(255,255,255,0.6)',
        fontFamily:'system-ui,sans-serif',
        lineHeight:1.6, textAlign:'center', maxWidth:260,
        fontStyle:'italic', marginBottom:14,
      }}>
        Design the dress that becomes you.
      </div>

      {/* Begin button */}
      <button onClick={onBegin} style={{
        padding:'11px 32px', borderRadius:999,
        backgroundColor:'#C9A0A0', border:'none',
        color:'#2D2926', fontSize:10, fontWeight:700,
        letterSpacing:'0.25em', textTransform:'uppercase',
        cursor:'pointer', fontFamily:'system-ui,sans-serif',
        boxShadow:'0 4px 24px rgba(201,160,160,0.35)',
      }}>
        Begin designing
      </button>

      <div style={{
        marginTop:36, fontSize:9, color:'rgba(255,255,255,0.15)',
        letterSpacing:'0.15em', fontFamily:'system-ui,sans-serif',
        textAlign:'center',
      }}>
        © 2026 Tabitha Studio. All rights reserved. ·{' '}
        <a href="/privacy.html" style={{ color:'rgba(255,255,255,0.25)', textDecoration:'underline' }}>Privacy Policy</a>
        {' · '}
        <a href="/terms.html" style={{ color:'rgba(255,255,255,0.25)', textDecoration:'underline' }}>Terms &amp; Conditions</a>
        {' · '}
        <a href="/faq.html" style={{ color:'rgba(255,255,255,0.25)', textDecoration:'underline' }}>FAQ</a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Content safety filter
// Runs before every Imagen request — blocks prohibited content silently
// ─────────────────────────────────────────────────────────────────────────────
const BLOCKED_TERMS = [
  // Nudity / explicit
  'nude','naked','topless','bottomless','explicit','nsfw','pornograph',
  'lingerie','underwear','bra','panties','nipple','genitali','sexual',
  'erotic','fetish','strip','undress','undressed','bare breast',
  'bare chest','exposed','semi-naked','half-naked',
  // Real people
  'kardashian','beyoncé','beyonce','taylor swift','rihanna','kim k',
  'celebrity','famous person','real person','politician','president',
  // Violence / harm
  'blood','gore','wound','injury','weapon','gun','knife','dead','corpse',
  // Hateful / discriminatory
  'racist','nazi','hate','slur',
  // Manipulation
  'ignore previous','ignore instructions','jailbreak','bypass','override safety',
  'disregard','forget instructions','new instructions','pretend you',
];

function resolveSelectedValue(value, customValue) {
  return value === "Custom" ? String(customValue || "").trim() : value;
}

function buildCanonicalDesignSpec(form) {
  const fabricEntry = FABRIC_LIBRARY.find(function(f) { return f.id === form.fabricId; });
  const fabricName = form.fabricId === "custom"
    ? String(form.customFabric || "").trim()
    : (fabricEntry?.name || form.fabricId || "Custom Fabric");

  const cut = resolveSelectedValue(form.cut, form.customCut) || "A-line";
  const neckline = resolveSelectedValue(form.neckline, form.customNeckline) || "V-Neck";
  const backNeckline = resolveSelectedValue(form.backNeckline, form.customBackNeckline) || "Low V-Back";
  const sleeves = resolveSelectedValue(form.sleeves, form.customSleeves) || "Sleeveless";
  const closure = resolveSelectedValue(form.closure, form.customClosure) || "Invisible Zip — Side Seam";
  const dressLength = resolveSelectedValue(form.dressLength, form.customDressLength) || "Midi";
  const skirtType = resolveSelectedValue(form.skirtType, form.customSkirtType) || "A-line";
  const skirtLength = resolveSelectedValue(form.skirtLength, form.customSkirtLength) || "Midi";
  const jacketNeckline = resolveSelectedValue(form.jacketNeckline, form.customJacketNeckline) || "Notch Lapel";
  const jacketSleeves = resolveSelectedValue(form.jacketSleeves, form.customJacketSleeves) || "Long Sleeve";

  return {
    category: form.category,
    fabricId: form.fabricId,
    fabricName,
    colorDescription: form.colorDescription,
    colorHex: form.colorHex,
    lining: form.lining,
    separates: !!form.separates,
    cut,
    neckline,
    backNeckline,
    sleeves,
    closure,
    dressLength,
    frontDescription: form.frontDescription,
    backDescription: form.backDescription,
    skirtType,
    skirtLength,
    jacketNeckline,
    jacketSleeves,
    jacketFront: form.jacketFront,
    jacketBack: form.jacketBack,
    skirtFront: form.skirtFront,
    skirtBack: form.skirtBack,
    atmosphere: form.atmosphere,
    designDetails: { ...DEFAULT_DESIGN_DETAILS, ...(form.designDetails || {}) },
    designDetailsSummary: form.designDetailsSummary || buildDesignDetailsSummary({ ...DEFAULT_DESIGN_DETAILS, ...(form.designDetails || {}) }),
    originalComments: form.originalComments || '',
    aiInterpretedComments: form.aiInterpretedComments || '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Canonical garment anchor — shared string used by both Studio and Salon
// prompts to ensure consistent design interpretation across both render calls
// ─────────────────────────────────────────────────────────────────────────────
function buildCanonicalGarmentAnchor(spec) {
  const silhouetteDesc  = SILHOUETTE_IMAGEN_DESC[spec.cut] || spec.cut || "A-line";
  const fabricDesc      = FABRIC_IMAGEN_DESC[spec.fabricId] || spec.fabricName || "fabric";
  const allColours      = COLOUR_PALETTE.flatMap(function(g) { return g.colours; });
  const paletteColour   = allColours.find(function(c) { return c.name === spec.colorDescription; });
  const imagenColourDesc = paletteColour ? paletteColour.imagenDesc : (spec.colorDescription || "neutral");
  const hexColour       = spec.colorHex || "#c9a0a0";

  const garmentDesc = buildCompositeGarmentDesc(spec);

  const colourLock =
    `COLOUR: ${imagenColourDesc} (hex ${hexColour}) throughout the entire garment — ` +
    `do not shift lighter, darker, cooler, warmer, or more saturated. ` +
    `This exact hex must be matched throughout the entire garment.`;

  const hemLogic = DRESS_LENGTH_HEM_LOGIC[spec.dressLength] || `${String(spec.dressLength || "midi").toLowerCase()} length`;

  return { garmentDesc, fabricDesc, imagenColourDesc, hexColour, colourLock, hemLogic };
}

function buildLockedRenderPrompts(spec) {
  const activeGoal = DESIGN_GOALS.find(function(c) { return c.id === spec.category; });
  let garmentDesc;
  let frontDetail;
  let backDetail;
  let hemLogic;

  // Use canonical anchor for consistent garment description across Studio + Salon
  const canonical = buildCanonicalGarmentAnchor(spec);
  const { fabricDesc, imagenColourDesc: _ignored, colourLock: _colourLock } = canonical;
  garmentDesc = canonical.garmentDesc;
  hemLogic    = canonical.hemLogic;

  if (spec.separates) {
    frontDetail = [spec.jacketFront, spec.skirtFront].filter(Boolean).join(" ").trim();
    backDetail  = [spec.jacketBack,  spec.skirtBack].filter(Boolean).join(" ").trim();
    hemLogic = DRESS_LENGTH_HEM_LOGIC[spec.skirtLength] || `${String(spec.skirtLength || "midi").toLowerCase()} length`;
  } else {
    frontDetail = spec.frontDescription || "";
    backDetail  = spec.backDescription  || "";
  }

  // When no detail is specified, explicitly lock out improvisation
  const userNotesBolster = spec.userNotes ? `Additional detail requested: ${spec.userNotes}.` : "";
  // Surface treatment gets elevated language so Imagen treats it as a render requirement
  const surfaceBolster = (spec.designDetails?.surface && spec.designDetails.surface !== "None")
    ? `VISIBLE ON GARMENT: ${spec.designDetails.surface} — must be clearly rendered.`
    : "";
  const designDetailsBolster = spec.designDetailsSummary
    ? (surfaceBolster ? surfaceBolster + ` Full design: ${spec.designDetailsSummary}` : `Design details: ${spec.designDetailsSummary}`)
    : "";
  // Sanitise user notes — strip negative instructions that cause Imagen to render the very thing being refused
  // e.g. "NO PEPLUM" causes Imagen to render a peplum; replace with positive equivalents
  function sanitiseForImagen(text) {
    return text
      .replace(/no\s+peplum/gi,          "single-piece dress with no peplum or overskirt")
      .replace(/no\s+beading\s+below/gi, "skirt is plain with no beading below")
      .replace(/do\s+not\s+add\s+bead/gi,"skirt is plain velvet only")
      .replace(/don'?t\s+add\s+bead/gi,  "skirt is plain velvet only")
      .replace(/no\s+jacket/gi,           "one-piece dress, no jacket")
      .replace(/no\s+split/gi,            "no split in skirt")
      .replace(/not\s+a\s+suit/gi,       "one-piece dress");
  }

  const rawFrontDetail = frontDetail
    ? sanitiseForImagen(`${frontDetail}${designDetailsBolster ? " " + designDetailsBolster : ""}${userNotesBolster ? " " + userNotesBolster : ""}`)
    : [designDetailsBolster, userNotesBolster].filter(Boolean).map(sanitiseForImagen).join(' ') || `Plain ${fabricDesc} — no embellishment, no appliqué, no lace, no cutouts, no added decoration. Exactly as the silhouette specifies.`;
  // Add a skirt-clean guard when embellishment is described but user hasn't indicated allover/skirt intent
  const hasEmbellishment = /bead|embroid|appliqu|lace|filigree|sequin|crystal|pearl|trim|ruffle|feather|fringe/i.test(rawFrontDetail);
  const hasAlloverIntent = /allover|all.?over|throughout|full.length|entire dress|head to toe/i.test(rawFrontDetail);
  const skirtCleanGuard = (hasEmbellishment && !hasAlloverIntent)
    ? ` CRITICAL: THE SKIRT MUST BE COMPLETELY PLAIN ${fabricDesc.toUpperCase()} WITH ZERO EMBELLISHMENT. DO NOT ADD ANY BEADING, EMBROIDERY, APPLIQUÉ, LACE, OR DECORATION TO THE SKIRT OR BELOW THE WAIST SEAM. ALL EMBELLISHMENT IS ON THE BODICE ONLY, ABOVE THE WAIST. THIS IS A SINGLE ONE-PIECE DRESS — THE BODICE AND SKIRT ARE ONE CONTINUOUS GARMENT. THE WAIST SEAM IS A CONSTRUCTION SEAM ONLY, NOT A STRUCTURAL SEPARATION. NO PEPLUM. NO OVERSKIRT. NO FLOUNCE. NO SEPARATE LAYER. THE SKIRT FALLS CLEANLY FROM THE WAIST SEAM TO THE HEM WITH NO INTERRUPTION.`
    : "";
  const frontDetailFinal = rawFrontDetail + skirtCleanGuard;
  const backDetailFinal  = backDetail  || [designDetailsBolster, `Plain ${fabricDesc} back — no embellishment, no cutouts, no open back unless specified. Matches front fabric exactly.`].filter(Boolean).join(' ');

  // Use canonical colour values
  const imagenColourDesc = canonical.imagenColourDesc;

  // Surface treatment requires special handling — Imagen needs it as a PRIORITY instruction
  // not buried after the negative embellishment guard
  const hasSurfaceDetail = spec.designDetails?.surface && spec.designDetails.surface !== "None";
  const hasStructuralDetail = spec.designDetails && (
    (spec.designDetails.bodice && spec.designDetails.bodice !== "Clean") ||
    (spec.designDetails.waist && spec.designDetails.waist !== "Clean waist") ||
    (spec.designDetails.skirtBehavior && spec.designDetails.skirtBehavior !== "Clean fall") ||
    (spec.designDetails.skirtFeature && spec.designDetails.skirtFeature !== "None")
  );
  const hasAnyDetail = hasSurfaceDetail || hasStructuralDetail ||
    spec.frontDescription || spec.backDescription || spec.userNotes;

  // ── Atmosphere / scene ────────────────────────────────────────────────────
  const atmosphereText = spec.atmosphere || GOAL_ATMOSPHERE[spec.category] || "an elegant setting";
  const hasOrchestra = /orchestra|orchestral|musicians|string quartet|live music|live band/i.test(atmosphereText);
  const sceneLock = `Scene: ${atmosphereText}.${hasOrchestra ? " A live orchestra with musicians and instruments must be clearly visible in the background." : ""}`;

  // ── Closure negative ──────────────────────────────────────────────────────
  const isSideSeam = (spec.closure || "").toLowerCase().includes("side seam");
  const closureNegative = isSideSeam
    ? "No centre back seam, no centre back zip."
    : "";

  // ── Negative prompt ───────────────────────────────────────────────────────
  // Keep this focused — over-long negative prompts consume attention budget
  // away from the actual design detail.
  const baseNegativeStructural = (spec.separates ? "diptych, split-screen, collage, triptych, front and back in one frame, side-by-side, contact sheet, two models, ghost mannequin, mannequin, dress form, garment only, illustration, painting, CGI, avatar, cropped head, head cut off, forehead cut off, shoulders cut off, faceless, headless, partial figure, bust-up crop, mid-shot, zoomed in detail, close-up" : "diptych, split-screen, collage, triptych, front and back in one frame, side-by-side, contact sheet, two models, ghost mannequin, mannequin, dress form, garment only, illustration, painting, CGI, avatar, cropped head, head cut off, forehead cut off, shoulders cut off, faceless, headless, partial figure, bust-up crop, mid-shot, zoomed in detail, close-up, peplum jacket, separate jacket, two-piece suit, blazer");
  const baseNegativePlain = ", embellishment, appliqué, lace overlay, embroidery, floral detail";
  const skirtEmbellishmentNegative = (hasEmbellishment && !hasAlloverIntent)
    ? ", beading on skirt, embroidery on skirt, appliqué on skirt, embellishment below waist, peplum, overskirt, flounce"
    : "";
  const negativePrompt = baseNegativeStructural +
    (hasEmbellishment ? "" : baseNegativePlain) +
    skirtEmbellishmentNegative +
    (closureNegative ? ", " + closureNegative : "") +
    (hasOrchestra ? ", empty background, plain background, studio background" : "");

  // ── Prompt blocks — ordered by priority ──────────────────────────────────
  // Scene comes first — Imagen weights early tokens heavily.
  // Background stated last = ignored in favour of grey studio default.
  const cameraBlock =
    `ONE photograph. ONE model. FRONT VIEW ONLY. ` +
    `Photorealistic full-length editorial fashion photograph. ` +
    `${sceneLock} ` +
    `Single continuous frame — absolutely not a diptych, not split into two panels, not front-and-back side by side, not a composite. ` +
    `One tall slender high-fashion model facing directly toward camera, wearing the garment, standing within the scene. ` +
    `Camera pulled back to show the complete figure: full head and face with clear space above, feet on the ground below. ` +
    `Model occupies two-thirds of frame height; the setting is clearly visible on all sides. ` +
    `Do not crop the head. Do not zoom in on any detail. Do not show the back of the garment.`;

  // GARMENT STRUCTURE (colour, silhouette, length, neckline, sleeves)
  const garmentBlock =
    `Garment: ${garmentDesc}. ` +
    `Fabric: ${fabricDesc}. ` +
    `${canonical.colourLock} ` +
    `HEM LENGTH MUST BE ${hemLogic} — do not alter the hem length under any circumstances. Neckline: ${spec.separates ? spec.jacketNeckline : spec.neckline}. Sleeves: ${spec.separates ? spec.jacketSleeves : spec.sleeves}. ` +
    (hasStructuralDetail ? `Construction: ${spec.designDetailsSummary} ` : ``);

  // SURFACE DETAIL (embellishment stated positively — no competing prohibitions when detail exists)
  const detailBlock = hasAnyDetail
    ? `Design detail — render exactly as described: ${frontDetailFinal}`
    : `Plain, unembellished garment. No beading, embroidery, appliqué, lace, or added decoration.`;

  // ── Assembled prompts ─────────────────────────────────────────────────────
  const frontPrompt = `${cameraBlock} ${garmentBlock} ${detailBlock} No text, no watermark.`;

  const frontFallbackPrompt =
    `Photorealistic editorial photograph. ${sceneLock} ` +
    `Single-model full-length front view — one frame, not a diptych. ` +
    `Full head visible at top, feet at bottom. Do not crop. ` +
    `${garmentDesc}. ${spec.fabricName}. ${imagenColourDesc} (${spec.colorHex}). ` +
    `Hem: ${hemLogic}. ` +
    (hasAnyDetail ? `Detail: ${frontDetailFinal}` : `Plain, no embellishment.`);

  const frontEmergencyPrompt =
    `ONE photo. ONE woman. FRONT VIEW ONLY. Not a diptych. Not split. Not two panels. ` +
    `${sceneLock} Full body, head to toe, facing camera. Head fully visible at top. ` +
    `${garmentDesc}. ${imagenColourDesc}. Hem must be ${hemLogic}. ` +
    (hasAnyDetail ? frontDetailFinal : `Plain ${spec.fabricName}.`) +
    ` Single continuous image. No back view. No side-by-side.`;

  return {
    frontPrompt,
    frontFallbackPrompt,
    frontEmergencyPrompt,
    negativePrompt,
  };
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function checkContentSafety(form) {
  // Look up Imagen-safe colour description — don't check the display name against blocked terms
  const allColours = COLOUR_PALETTE.flatMap(function(g) { return g.colours; });
  const paletteColour = allColours.find(function(c) { return c.name === form.colorDescription; });

  const namedFields = [
    { label: "Front Detailing",   value: form.frontDescription },
    { label: "Back Detailing",    value: form.backDescription },
    { label: "Setting",           value: form.atmosphere },
    { label: "Custom Fabric",     value: form.customFabric },
    { label: "Custom Silhouette", value: form.customCut },
    { label: "Custom Neckline",      value: form.customNeckline },
    { label: "Custom Back Neckline", value: form.customBackNeckline },
    { label: "Custom Closure",       value: form.customClosure },
    { label: "Custom Sleeves",    value: form.customSleeves },
    // Only check colour if it's a freeform entry — skip palette colours entirely
    ...(!paletteColour && form.colorDescription ? [{ label: "Colour", value: form.colorDescription }] : []),
  ];
  const SAFE_EXCEPTIONS = ["corset", "plunging", "underskirt", "bare back", "bare shoulder", "exposed back", "exposed shoulder"];
  const safeContainers  = ["underskirt", "corset", "structured", "plunging neckline", "exposed back", "exposed shoulder", "bare back", "bare shoulder"];
  const SAFE_FASHION_TERMS = [
    "button", "buttons", "covered button", "covered buttons", "cloth-covered button", "cloth-covered buttons",
    "bodice", "boning", "corset", "seam", "side seam", "ruching", "pleat", "pleating",
    "beading", "embroidery", "applique", "appliqué", "waist seam", "center back", "centre back"
  ];

  for (const field of namedFields) {
    if (!field.value) continue;
    const val = field.value.toLowerCase();
    if (SAFE_EXCEPTIONS.some(function(ex) { return val === ex || val.startsWith(ex); })) continue;
    if (SAFE_FASHION_TERMS.some(function(term) { return val.includes(term); })) continue;

    const blocked = BLOCKED_TERMS.find(function(term) {
      if (safeContainers.some(function(safe) { return safe.includes(term) && val.includes(safe); })) return false;
      const regex = new RegExp(`\b${escapeRegExp(term)}\b`, 'i');
      return regex.test(val);
    });
    if (blocked) {
      return `Some wording in the ${field.label.toLowerCase()} may not be supported. Please simplify or rephrase it and try again.`;
    }
  }
  return null;
}

async function interpretSharedDetails(spec, cleanedDetails) {
  const system = [
    "You are a fashion design interpreter for a bespoke atelier interface.",
    "The user's selected silhouette, garment structure, hem length, neckline, sleeves, fabric, and color are locked and must remain dominant.",
    "The user provides one shared notes field describing any desired added detail.",
    "Your job is to translate that shared note into frontDescription and backDescription for rendering.",
    "Do not change or override silhouette, color, fabric, neckline, sleeves, hem length, garment type, or overall shape.",
    "Embellishment is optional and must only appear when explicitly requested in the user's shared notes.",
    "Only include beading, embroidery, appliqué, lace overlay, trim, buttons, or other embellishment when explicitly requested in the user's shared notes.",
    "If the user does not explicitly request embellishment, keep the detailing clean and structurally supportive.",
    "Front and back details may add seam placement, drape, ruching, pleating, paneling, closures, or construction emphasis only.",
    "Ask at most 2 concise clarifying questions when ambiguity remains.",
    "Return JSON only with keys frontDescription, backDescription, questions.",
  ].join("\n");

  const user = {
    selections: spec,
    additional_details: cleanedDetails,
  };

  const data = await callAnthropicMessages({
    model: "claude-sonnet-4-6",
    max_tokens: 500,
    system,
    messages: [{ role: "user", content: JSON.stringify(user) }],
  });

  const raw = (data.content || []).map(function(block) { return block.text || ""; }).join("").trim();
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  const parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(raw);

  return {
    frontDescription: parsed.frontDescription || "",
    backDescription: parsed.backDescription || "",
    questions: Array.isArray(parsed.questions) ? parsed.questions.slice(0, 2) : [],
  };
}

async function refineSharedDetails(spec, cleanedDetails) {
  const system = [
    "You are a fashion design writing assistant for a bespoke atelier interface.",
    "The user's selected silhouette, garment structure, hem length, neckline, sleeves, fabric, and color are already chosen and locked.",
    "Your job is to turn rough, fragmentary, or vague user notes into one clean shared description for the additional comments field.",
    "Do not rewrite the whole garment. Do not change silhouette, garment type, hem length, neckline, sleeves, fabric, or color.",
    "Only describe details added beyond the locked selections, such as beading, embroidery, buttons, ruching, pleating, drape, seams, or construction emphasis.",
    "If the user did not explicitly ask for embellishment, do not invent embellishment.",
    "Keep the refined description concise, concrete, and easy for a client to recognize as their idea.",
    "The refined description must be 400 characters or fewer — be precise and capture the key details.",
    "Also provide frontDescription and backDescription for rendering, based only on the user's requested details.",
    "Ask at most 2 concise clarifying questions when ambiguity remains.",
    "Return JSON only with keys refinedDetails, frontDescription, backDescription, questions.",
  ].join("\n");

  const user = {
    selections: spec,
    rough_details: cleanedDetails,
  };

  const data = await callAnthropicMessages({
    model: "claude-sonnet-4-6",
    max_tokens: 500,
    system,
    messages: [{ role: "user", content: JSON.stringify(user) }],
  });

  const raw = (data.content || []).map(function(block) { return block.text || ""; }).join("").trim();
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  const parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(raw);

  return {
    refinedDetails: parsed.refinedDetails || cleanedDetails || "",
    frontDescription: parsed.frontDescription || "",
    backDescription: parsed.backDescription || "",
    questions: Array.isArray(parsed.questions) ? parsed.questions.slice(0, 2) : [],
  };
}

async function interpretReferenceImage(base64Image, mimeType, currentForm) {
  const system = [
    "You are a fashion design detail interpreter for a bespoke atelier.",
    "The user has already chosen their garment structure: silhouette, fabric, colour, neckline, sleeves, and length.",
    "They have uploaded a reference photo to help describe design details that are hard to put into words.",
    "Your job is ONLY to read visible surface details from the image: embellishment, beading, lace, draping, pleating, ruching, trim, applique, and construction details.",
    "Do NOT report on silhouette, fabric type, colour, neckline shape, sleeve style, or dress length - the user has already chosen these.",
    "Be specific about placement: e.g. 'gold filigree beading across entire bodice', 'soft draping gathered at right hip', 'beaded cap sleeves', 'pleating at centre front waist'.",
    "You must return a JSON object with exactly these keys:",
    "  details: plain-English description of visible design details only - max 350 characters. Focus on what makes this dress distinctive beyond its basic structure. Empty string if the dress is plain.",
    "  notes: one sentence confirming what details you read, so the user can verify you read the photo correctly.",
    "Only describe what is clearly visible. Do not invent. Return JSON only - no preamble, no markdown.",
  ].join("\n");

  const data = await callAnthropicMessages({
    model: "claude-sonnet-4-6",
    max_tokens: 600,
    system,
    messages: [{
      role: "user",
      content: [
        {
          type: "image",
          source: { type: "base64", media_type: mimeType, data: base64Image },
        },
        {
          type: "text",
          text: "Please read this dress photograph and extract the design details as instructed.",
        }
      ]
    }],
  });

  const raw = (data.content || []).map(function(block) { return block.text || ""; }).join("").trim();
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  return jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(raw);
}

// ─────────────────────────────────────────────────────────────────────────────
// Sign-in gate modal — collects email and sends a magic link
// ─────────────────────────────────────────────────────────────────────────────
function SignInGateModal({ onSignIn, onClose }) {
  const [email,   setEmail]   = useState("");
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState(null);

  function handleSend() {
    if (!email.trim()) return;
    setSending(true);
    setError(null);
    onSignIn(email.trim()).then(function() {
      setSent(true);
      setSending(false);
    }).catch(function(err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSending(false);
    });
  }

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:200,
      backgroundColor:'rgba(26,23,20,0.72)',
      backdropFilter:'blur(6px)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'24px 20px',
      animation:'fadeIn 0.2s ease',
    }} onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        backgroundColor:'#fff', borderRadius:20,
        padding:'32px 28px', maxWidth:360, width:'100%',
        boxShadow:'0 30px 80px rgba(0,0,0,0.35)',
        textAlign:'center',
      }}>
        <div style={{
          width:48, height:48, borderRadius:'50%',
          backgroundColor:'#fdf3f3', border:'1px solid #e9dde4',
          display:'flex', alignItems:'center', justifyContent:'center',
          margin:'0 auto 18px',
        }}>
          <Lock style={{width:20, height:20, color:'#C9A0A0'}} />
        </div>

        {sent ? (
          <>
            <div style={{fontSize:15, fontWeight:700, color:'#2D2926', marginBottom:8, fontFamily:'system-ui,sans-serif', letterSpacing:'-0.01em'}}>
              Check your email
            </div>
            <p style={{margin:'0 0 24px', fontSize:12, color:'#78716c', lineHeight:1.65, fontFamily:'system-ui,sans-serif'}}>
              We sent a sign-in link to <strong>{email}</strong>. Click it to save your design — this tab will stay open.
            </p>
            <button
              onClick={onClose}
              style={{
                display:'block', width:'100%',
                padding:'12px 0', borderRadius:12,
                border:'1px solid #e7e5e4', backgroundColor:'transparent',
                color:'#78716c', fontSize:10, fontWeight:700,
                letterSpacing:'0.2em', textTransform:'uppercase',
                cursor:'pointer', fontFamily:'system-ui,sans-serif',
              }}
            >
              Continue designing
            </button>
          </>
        ) : (
          <>
            <div style={{fontSize:15, fontWeight:700, color:'#2D2926', marginBottom:8, fontFamily:'system-ui,sans-serif', letterSpacing:'-0.01em'}}>
              Sign in to save your design
            </div>
            <p style={{margin:'0 0 20px', fontSize:12, color:'#78716c', lineHeight:1.65, fontFamily:'system-ui,sans-serif'}}>
              We'll send a sign-in link to your email — no password needed.
            </p>
            <input
              autoFocus
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={function(e) { setEmail(e.target.value); setError(null); }}
              onKeyDown={function(e) { if (e.key === "Enter") handleSend(); }}
              style={{
                display:'block', width:'100%', boxSizing:'border-box',
                padding:'12px 14px', borderRadius:12, marginBottom:10,
                border: error ? '1px solid #c0604a' : '1px solid #D8D0C8',
                fontSize:13, fontFamily:'system-ui,sans-serif',
                color:'#2D2926', outline:'none',
              }}
            />
            {error && (
              <div style={{fontSize:10, color:'#c0604a', fontFamily:'system-ui,sans-serif', marginBottom:10, textAlign:'left'}}>{error}</div>
            )}
            <button
              onClick={handleSend}
              disabled={sending || !email.trim()}
              style={{
                display:'block', width:'100%',
                padding:'14px 0', borderRadius:12, border:'none',
                backgroundColor:'#C9A0A0', color:'#2D2926',
                fontSize:10, fontWeight:700, letterSpacing:'0.25em', textTransform:'uppercase',
                cursor: sending ? 'default' : 'pointer', fontFamily:'system-ui,sans-serif',
                boxShadow:'0 4px 16px rgba(201,160,160,0.4)',
                marginBottom:10, opacity: sending ? 0.6 : 1,
              }}
            >
              {sending ? "Sending…" : "Send Sign-In Link"}
            </button>
            <button
              onClick={onClose}
              style={{
                display:'block', width:'100%',
                padding:'12px 0', borderRadius:12,
                border:'1px solid #e7e5e4', backgroundColor:'transparent',
                color:'#78716c', fontSize:10, fontWeight:700,
                letterSpacing:'0.2em', textTransform:'uppercase',
                cursor:'pointer', fontFamily:'system-ui,sans-serif',
              }}
            >
              Continue designing
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function App({
  initialForm = INITIAL_FORM,
  initialDesignRender = null,
  initialDesignBackRender = null,
  skipWelcome = false,
  hideHeader = false,
  initialDesignSeed = null,
  initialIsLocked = false,
  onStudioDataChange = function() {},
  onProceedToFittingRoom = function() {},
  onBackToCollection = function() {},
  onSaveToCollection = function() {},
  onSubmitToGallery = function() {},
  isSignedIn = false,
  onSignIn = function() {},
  onLoadBySeed = function() {},
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSignInGate, setShowSignInGate] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [seedSearchValue, setSeedSearchValue] = useState('');
  const [seedSearchError, setSeedSearchError] = useState('');
  const [seedSearchLoading, setSeedSearchLoading] = useState(false);
  const [referenceImageBase64, setReferenceImageBase64] = useState(null);
  const [referenceImageMime, setReferenceImageMime] = useState(null);
  const [referenceImageName, setReferenceImageName] = useState('');
  const [isInterpretingImage, setIsInterpretingImage] = useState(false);
  const [imageInterpretationNote, setImageInterpretationNote] = useState('');
  const [imageInterpretationError, setImageInterpretationError] = useState('');
  const [showWelcome, setShowWelcome] = useState(function() {
    // Skip if explicitly told to, or if returning from another room with a render
    if (skipWelcome) return false;
    return !initialDesignRender && !initialDesignBackRender;
  });

  function handleWelcomeBegin() {
    setShowWelcome(false);
    // Form stays as INITIAL_FORM defaults — no preset loaded, no lock
  }
  const [activeDesign, setActiveDesign] = useState(function() {
    const front = initialDesignRender || initialDesignBackRender || null;
    const back = initialDesignBackRender || initialDesignRender || null;
    return front || back ? { front, back } : null;
  });
  const [designSeed, setDesignSeed] = useState(initialDesignSeed || null);
  const [isLocked, setIsLocked]     = useState(initialIsLocked || false);
  const [savedToast, setSavedToast] = useState(false);
  const [isSaved, setIsSaved] = useState(!!initialDesignRender && isSignedIn);
  const [gallerySubmitState, setGallerySubmitState] = useState("idle"); // idle | submitting | done | error
  // editWarning replaced by showVersionModal
  const [error, setError]                = useState(null);
  const [form, setForm]                  = useState(() => {
    const merged = { ...INITIAL_FORM, ...(initialForm || {}) };
    const isFreshLoad = !initialDesignRender && !initialDesignBackRender;
    return {
      ...merged,
      dressLength: normalizeLegacyLength(merged.dressLength),
      skirtLength: normalizeLegacyLength(merged.skirtLength),
      designDetails: { ...DEFAULT_DESIGN_DETAILS, ...(merged.designDetails || {}) },
      designDetailsSummary: merged.designDetailsSummary || buildDesignDetailsSummary({ ...DEFAULT_DESIGN_DETAILS, ...(merged.designDetails || {}) }),
      originalComments: merged.originalComments || '',
      aiInterpretedComments: merged.aiInterpretedComments || '',
      // Always clear front/back descriptions on fresh load — never inherit stale AI-suggested text
      frontDescription: isFreshLoad ? '' : (merged.frontDescription || ''),
      backDescription:  isFreshLoad ? '' : (merged.backDescription  || ''),
    };
  });
  const [designSpec, setDesignSpec] = useState(function() {
    const merged = { ...INITIAL_FORM, ...(initialForm || {}) };
    return buildCanonicalDesignSpec(merged);
  });
  const [selectedSetting, setSelectedSetting] = useState('ballroom');
  const [refinedSuggestion, setRefinedSuggestion] = useState(null);
  const [interpreterQuestions, setInterpreterQuestions] = useState([]);
  const [isInterpreting, setIsInterpreting] = useState(false);

  const handleGoalChange = useCallback((goalId) => {
    const g = DESIGN_GOALS.find(c => c.id === goalId);
    if (!g) return;
    // Sync selected setting highlight with default for this category
    const defaultSettingId = CURATED_SETTINGS.find(s => s.description === g.defaultAtmosphere)?.id || null;
    if (defaultSettingId) setSelectedSetting(defaultSettingId);
    // Reset separates when switching away from Business
    setForm(prev => ({
      ...prev,
      category: goalId,
      fabricId: g.recommendedFabricId,
      atmosphere: g.defaultAtmosphere,
      frontDescription: "",
      backDescription: "",
      designDetails: { ...DEFAULT_DESIGN_DETAILS },
      designDetailsSummary: buildDesignDetailsSummary(DEFAULT_DESIGN_DETAILS),
      originalComments: '',
      aiInterpretedComments: '',
      dressLength: GOAL_DEFAULT_DRESS_LENGTH[goalId] || prev.dressLength || "Midi",
      customDressLength: "",
      separates: goalId === 'business' ? prev.separates : false,
    }));
  }, []);

  const set    = (key) => (e) => {
    if (isLocked && designSeed && isSaved) setShowVersionModal(true);
    setForm(prev => ({ ...prev, [key]: e.target.value }));
  };
  const toggle = (key) => () => {
    if (isLocked && designSeed && isSaved) setShowVersionModal(true);
    if (key === 'separates') {
      setForm(prev => ({
        ...prev,
        separates: !prev.separates,
        // Reset jacket to proper defaults when enabling separates
        ...(!prev.separates ? {
          jacketNeckline: 'Notch Lapel',
          jacketSleeves: 'Long Sleeve',
          jacketFront: '',
          jacketBack: '',
          skirtFront: '',
          skirtBack: '',
        } : {}),
      }));
    } else {
      setForm(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const setDesignDetail = useCallback((key) => (e) => {
    if (isLocked && designSeed && isSaved) setShowVersionModal(true);
    const value = e.target.value;
    setForm(function(prev) {
      const nextDesignDetails = { ...(prev.designDetails || DEFAULT_DESIGN_DETAILS), [key]: value };
      return {
        ...prev,
        designDetails: nextDesignDetails,
        designDetailsSummary: buildDesignDetailsSummary(nextDesignDetails),
      };
    });
  }, []);

  const commentValue = form.originalComments || '';
  const appliedCommentValue = form.aiInterpretedComments || '';

  const setOriginalComments = useCallback((value) => {
    setForm(function(prev) {
      return {
        ...prev,
        originalComments: value,
        aiInterpretedComments: '',
      };
    });
  }, []);
  const [focusedField, setFocusedField] = useState(null);
  const [showPromptTip, setShowPromptTip] = useState(false);

  useEffect(() => {
    onStudioDataChange({
      form,
      designSpec: designSpec || buildCanonicalDesignSpec(form),
      designSeed,
      designRender: activeDesign?.front || null,
      designRenderBack: null,
    });
  }, [form, designSpec, designSeed, activeDesign, onStudioDataChange]);

  const refineMyIdea = useCallback(async () => {
    const cleanedDetails = String(form.originalComments || "").trim();
    if (!cleanedDetails) {
      setError("Add a few words first, then the assistant can refine them.");
      return;
    }

    const spec = buildCanonicalDesignSpec(form);
    setError(null);
    setIsInterpreting(true);

    try {
      const refined = await refineSharedDetails(spec, cleanedDetails);
      setRefinedSuggestion(refined);
      setInterpreterQuestions(refined.questions);
    } catch (err) {
      console.error("Refine idea failed:", err);
      setError("AI refinement failed. Please try again.");
    } finally {
      setIsInterpreting(false);
    }
  }, [form]);

  const applyRefinedIdea = useCallback(() => {
    if (!refinedSuggestion) return;
    setForm(function(prev) {
      return {
        ...prev,
        aiInterpretedComments: (refinedSuggestion.refinedDetails || prev.originalComments || '').slice(0, 400),
        frontDescription: refinedSuggestion.frontDescription || prev.frontDescription,
        backDescription: refinedSuggestion.backDescription || prev.backDescription,
      };
    });
    setInterpreterQuestions(refinedSuggestion.questions || []);
    setRefinedSuggestion(null);
  }, [refinedSuggestion]);

  const generate = useCallback(async () => {
    // Validate custom fields
    if (!form.separates) {
      if (form.cut==="Custom"&&!form.customCut.trim()){setError("Please describe your custom silhouette.");return;}
      if (form.neckline==="Custom"&&!form.customNeckline.trim()){setError("Please describe your custom neckline.");return;}
      if (form.backNeckline==="Custom"&&!form.customBackNeckline.trim()){setError("Please describe your custom back neckline.");return;}
      if (form.closure==="Custom"&&!form.customClosure.trim()){setError("Please describe your custom closure.");return;}
      if (form.sleeves==="Custom"&&!form.customSleeves.trim()){setError("Please describe your custom sleeves.");return;}
      if (form.dressLength==="Custom"&&!form.customDressLength.trim()){setError("Please describe your custom dress length.");return;}
    } else {
      if (form.skirtType==="Custom"&&!form.customSkirtType.trim()){setError("Please describe your custom skirt type.");return;}
      if (form.jacketNeckline==="Custom"&&!form.customJacketNeckline.trim()){setError("Please describe your custom jacket neckline.");return;}
      if (form.jacketSleeves==="Custom"&&!form.customJacketSleeves.trim()){setError("Please describe your custom jacket sleeves.");return;}
    }
    if (form.fabricId==="custom"&&!form.customFabric.trim()){setError("Please describe your custom fabric.");return;}

    // Content safety check
    const safetyError = checkContentSafety(form);
    if (safetyError) { setError(safetyError); return; }

    setError(null); setIsGenerating(true);

    // Use designSeed if set (locked or manually entered), otherwise generate new
    const seed = designSeed ? designSeed : Math.floor(Math.random() * 1000000);

    try {
      const spec = buildCanonicalDesignSpec(form);
      const cleanedDetails = String(form.aiInterpretedComments || form.originalComments || "").trim();

      if (cleanedDetails) {
        spec.userNotes = cleanedDetails;
      }

      if (cleanedDetails) {
        try {
          const interpreted = await interpretSharedDetails(spec, cleanedDetails);
          if (interpreted.frontDescription) spec.frontDescription = interpreted.frontDescription;
          if (interpreted.backDescription) spec.backDescription = interpreted.backDescription;

          setForm(function(prev) {
            return {
              ...prev,
              frontDescription: interpreted.frontDescription || prev.frontDescription,
              backDescription: interpreted.backDescription || prev.backDescription,
            };
          });
          setInterpreterQuestions(interpreted.questions);
        } catch (err) {
          console.error("Interpreter failed during generate:", err);
          spec.frontDescription = spec.frontDescription || cleanedDetails;
          spec.backDescription = spec.backDescription || cleanedDetails;
        }
      }

      setDesignSpec(spec);

      const {
        frontPrompt,
        frontFallbackPrompt,
        frontEmergencyPrompt,
        negativePrompt,
      } = buildLockedRenderPrompts(spec);

      const renderWithFallback = async (primaryPrompt, fallbackPrompt) => {
        try {
          return await callImagenWithRetry({
            prompt: primaryPrompt,
            negativePrompt,
            aspectRatio: "9:16",
            seed: seed,  // Use seed for consistency
          });
        } catch (err) {
          const keyError = /api key not valid/i.test(err?.message || "");
          if (err?.status === 400 && !keyError) {
            // Retry once with a more compact prompt when Imagen rejects verbose prompts.
            return callImagenWithRetry({
              prompt: fallbackPrompt,
              negativePrompt,
              aspectRatio: "9:16",
              seed: seed,  // Use same seed on fallback
            });
          }
          throw err;
        }
      };

      const renderStrictSingleView = async (primaryPrompt, fallbackPrompt, emergencyPrompt) => {
        const maxAttempts = 3;

        // First attempt with primary prompt
        let imageUrl = await renderWithFallback(primaryPrompt, fallbackPrompt);
        let isComposite = await looksLikeSplitComposite(imageUrl);

        // If composite detected, try up to 2 more times with emergency prompt
        let attempt = 1;
        while (isComposite && attempt < maxAttempts) {
          console.log(`Composite detected on attempt ${attempt}, retrying with emergency prompt...`);
          await sleep(1500); // Brief delay before retry

          imageUrl = await callImagenWithRetry({
            prompt: emergencyPrompt,
            negativePrompt,
            aspectRatio: "9:16",
            seed: seed,  // Use same seed on retry
          });

          isComposite = await looksLikeSplitComposite(imageUrl);
          attempt++;
        }

        if (isComposite) {
          console.warn(`Still detected composite after ${maxAttempts} attempts, using last result`);
        }

        return imageUrl;
      };

      const frontUrl = await renderStrictSingleView(frontPrompt, frontFallbackPrompt, frontEmergencyPrompt);

      setActiveDesign({ front: frontUrl });
      setDesignSeed(seed);
      setIsLocked(true);
    } catch (err) {
      console.error("Generation failed:", err);
      setError(err?.userMessage || "Render failed. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [form, isLocked, designSeed]);
  // ── Live hex sync from color description ──────────────────────────────────
  useEffect(() => {
    // Skip sync if description matches a palette entry — the palette onChange
    // already sets the correct hex, and partial-word matching would clobber it
    // (e.g. "Ice Blue" → partial match on "blue" → wrong dark hex)
    const allPaletteColours = COLOUR_PALETTE.flatMap(function(g) { return g.colours; });
    const isPaletteColour = allPaletteColours.some(function(c) { return c.name === form.colorDescription; });
    if (isPaletteColour) return;
    const resolvedHex = resolveColorHex(form.colorDescription);
    if (!resolvedHex) return;
    const currentHex = String(form.colorHex || "").trim().toLowerCase();
    if (currentHex === resolvedHex) return;
    setForm(prev => ({ ...prev, colorHex: resolvedHex }));
  }, [form.colorDescription]);

  const isBusiness = form.category === 'business';

  // ── Branded image compositor ─────────────────────────────────────────────────
  // Draws the source image onto a canvas, burns in a unified "Tabitha · Design No." label
  // top-left as a single pill, returns a branded JPEG Blob.
  // Uses arcTo rounded-rect for full cross-browser compatibility (no roundRect).
  const buildBrandedBlob = async function(src) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise(function(resolve, reject) {
      img.onload = resolve;
      img.onerror = reject;
      img.src = src;
    });

    const canvas = document.createElement("canvas");
    canvas.width  = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    const scale  = canvas.width / 390;
    const fs     = Math.round(11 * scale);
    const pad    = Math.round(8  * scale);
    const margin = Math.round(10 * scale);
    const cr     = Math.round(14 * scale); // corner radius

    function pillRect(x, y, w, h, bgAlpha, bgColor) {
      ctx.save();
      ctx.globalAlpha = bgAlpha;
      ctx.fillStyle   = bgColor;
      ctx.beginPath();
      ctx.moveTo(x + cr, y);
      ctx.lineTo(x + w - cr, y);
      ctx.arcTo(x + w, y,      x + w, y + cr,     cr);
      ctx.lineTo(x + w, y + h - cr);
      ctx.arcTo(x + w, y + h,  x + w - cr, y + h, cr);
      ctx.lineTo(x + cr, y + h);
      ctx.arcTo(x,      y + h,  x, y + h - cr,    cr);
      ctx.lineTo(x,      y + cr);
      ctx.arcTo(x,      y,      x + cr, y,         cr);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // ── Top-left: unified brand label — "Tabitha  ·  Design No. XXXXXX" ──
    // Two font weights in one pill: bold "Tabitha" + light separator + regular design no.
    const pillH = Math.round(fs * 2.2);
    const pillR = Math.round(pillH / 2); // fully rounded ends

    const seed = designSeed;
    const labelText = seed ? ("Tabitha  ·  Design No. " + seed) : "Tabitha";

    // Measure with the heavier weight first (worst case width)
    ctx.font = "700 " + fs + "px system-ui, -apple-system, sans-serif";
    const pillW = ctx.measureText(labelText).width + pad * 3;

    // Draw pill background — soft dark, semi-transparent
    pillRect(margin, margin, pillW, pillH, 0.62, "#1a1714");

    // Draw text — single pass, white
    ctx.save();
    ctx.globalAlpha  = 0.92;
    ctx.fillStyle    = "#ffffff";
    ctx.textBaseline = "middle";
    ctx.font = "700 " + fs + "px system-ui, -apple-system, sans-serif";
    ctx.fillText(labelText, margin + pad * 1.5, margin + pillH / 2);
    ctx.restore();

    return new Promise(function(resolve) {
      canvas.toBlob(resolve, "image/jpeg", 0.93);
    });
  };

  return (
    <div style={S.page}>
      <style>{`*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; } input::placeholder, textarea::placeholder { font-style: italic; color: #ada3ab; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Tabitha welcome overlay — first visit only */}
      {showWelcome && (
        <TabithaWelcome
          onBegin={handleWelcomeBegin}
        />
      )}

      {/* Header */}
      {!hideHeader && (
        <div style={S.header}>
          <Layers style={{color:C, marginRight:12, width:24, height:24, flexShrink:0}} />
          <div>
            <div style={{margin:0, padding:0, fontSize:21, fontWeight:700, letterSpacing:'-0.02em', color:C}}>Tabitha</div>
            <div style={{margin:0, padding:0, fontSize:10, fontWeight:700, letterSpacing:'0.24em', color:'#6f555f', textTransform:'uppercase'}}>Design Studio</div>
          </div>
        </div>
      )}

      <div style={S.main}>

        {/* ── Design Goal ── */}
        <div style={{margin:0, padding:0}}>
          <div style={{...S.lbl, marginBottom:10, color:'#DCCAD3'}}>Design Goal</div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8}}>
            {DESIGN_GOALS.map(cat => {
              const on = form.category === cat.id;
              return (
                <button key={cat.id} onClick={() => handleGoalChange(cat.id)} style={{
                  margin:0, padding:'14px 6px', borderRadius:12,
                  border:`1px solid ${on ? '#C9A0A0' : '#e7e5e4'}`,
                  backgroundColor: on ? '#C9A0A0' : '#ffffff', color: on ? '#2D2926' : '#57534e',
                  fontWeight: on ? 700 : 600,
                  cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:6,
                  fontFamily:'system-ui,sans-serif',
                }}>
                  <cat.Icon style={{width:15, height:15}} />
                  <span style={{margin:0, padding:0, fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', lineHeight:1.3, textAlign:'center'}}>
                    {cat.name}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Separates toggle — Business only */}
          {isBusiness && (
            <div style={{marginTop:12, padding:'12px 16px', backgroundColor:'#f5f5f4', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div style={{margin:0, padding:0, fontSize:12, fontWeight:700, color:C, letterSpacing:'0.05em'}}>Separates</div>
                <div style={{margin:0, padding:0, fontSize:11, color:'#6f666e', marginTop:2}}>Design a jacket & skirt instead of a dress</div>
              </div>
              {/* Toggle switch */}
              <div
                onClick={toggle('separates')}
                style={{
                  position:'relative', width:44, height:24, borderRadius:12, cursor:'pointer',
                  backgroundColor: form.separates ? C : '#d6d3d1',
                  transition:'background-color 0.2s', flexShrink:0,
                }}
              >
                <div style={{
                  position:'absolute', top:3, left: form.separates ? 23 : 3,
                  width:18, height:18, borderRadius:'50%', backgroundColor:'#fff',
                  boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'left 0.2s',
                }} />
              </div>
            </div>
          )}
        </div>

        {/* ── Architecture ── */}
        <div style={S.card}>
          <div style={S.secHead}><Scissors style={{width:13,height:13}} />Architecture</div>

          {form.separates && isBusiness ? (
            /* ── SEPARATES LAYOUT ── */
            <div>

              {/* Jacket section */}
              <div style={S.subHead}>Jacket</div>
              <div style={{marginBottom:16}}>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
                  <SelectField field="jacketNeckline" lib={JACKET_NECKLINE_LIBRARY} customField="customJacketNeckline" ph="e.g., Wide peak lapel with satin facing..." label="Lapel / Collar" form={form} set={set} focusedField={focusedField} setFocusedField={setFocusedField} />
                  <SelectField field="jacketSleeves" lib={JACKET_SLEEVE_LIBRARY} customField="customJacketSleeves" ph="e.g., Three-quarter with a single jet button at the cuff..." label="Sleeves" form={form} set={set} focusedField={focusedField} setFocusedField={setFocusedField} />
                </div>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
                <div>
                  <label style={S.lbl}>Front Details</label>
                  <textarea style={{...S.inp, minHeight:72, resize:'vertical', fontStyle: focusedField === 'jacketFront' ? 'normal' : 'italic', color: focusedField === 'jacketFront' ? '#1c1917' : '#a8a29e', fontWeight: focusedField === 'jacketFront' ? 400 : 300, transition:'font-style 0.1s, color 0.1s'}} value={form.jacketFront} onChange={set('jacketFront')} onFocus={() => setFocusedField('jacketFront')} onBlur={() => setFocusedField(null)} />
                </div>
                <div>
                  <label style={S.lbl}>Back Details</label>
                  <textarea style={{...S.inp, minHeight:72, resize:'vertical', fontStyle: focusedField === 'jacketBack' ? 'normal' : 'italic', color: focusedField === 'jacketBack' ? '#1c1917' : '#a8a29e', fontWeight: focusedField === 'jacketBack' ? 400 : 300, transition:'font-style 0.1s, color 0.1s'}} value={form.jacketBack} onChange={set('jacketBack')} onFocus={() => setFocusedField('jacketBack')} onBlur={() => setFocusedField(null)} />
                </div>
              </div>

              <hr style={S.divider} />

              {/* Skirt section */}
              <div style={S.subHead}>Skirt</div>
              <div style={{marginBottom:16}}>
                <label style={S.lbl}>Skirt Type</label>
                <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6}}>
                  {SKIRT_TYPES.map(s => {
                    const on = form.skirtType === s;
                    return (
                      <button key={s} onClick={() => setForm(p => ({...p, skirtType:s}))} style={{
                        margin:0, padding:'9px 4px', borderRadius:10,
                        border:`1px solid ${on ? '#C9A0A0' : '#e7e5e4'}`,
                        backgroundColor: on ? '#C9A0A0' : '#ffffff',
                        color: on ? '#2D2926' : '#57534e',
                        fontWeight: on ? 700 : 600,
                        fontSize:11, cursor:'pointer', fontFamily:'system-ui,sans-serif',
                      }}>{s}</button>
                    );
                  })}
                </div>
                {form.skirtType === "Custom" && (
                  <textarea
                  style={{...S.inp, marginTop:8, minHeight:64, resize:'vertical',
                    fontStyle: focusedField === 'customSkirtType' ? 'normal' : 'italic',
                    color: focusedField === 'customSkirtType' ? '#1c1917' : '#a8a29e',
                    fontWeight: focusedField === 'customSkirtType' ? 400 : 300,
                    transition:'font-style 0.1s, color 0.1s',
                  }}
                  placeholder="e.g., Bias-cut with a front slit and subtle flare at the knee..."
                  value={form.customSkirtType}
                  onChange={set('customSkirtType')}
                  onFocus={() => setFocusedField('customSkirtType')}
                  onBlur={() => setFocusedField(null)}
                />
                )}
              </div>
              <div style={{marginBottom:16}}>
                <label style={S.lbl}>Skirt Length</label>
                <div style={{display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:6}}>
                  {SKIRT_LENGTHS.map(l => {
                    const on = form.skirtLength === l;
                    return (
                      <button key={l} onClick={() => setForm(p => ({...p, skirtLength:l}))} style={{
                        margin:0, padding:'9px 4px', borderRadius:10,
                        border:`1px solid ${on ? '#C9A0A0' : '#e7e5e4'}`,
                        backgroundColor: on ? '#C9A0A0' : '#ffffff',
                        color: on ? '#2D2926' : '#57534e',
                        fontWeight: on ? 700 : 600,
                        fontSize:11, cursor:'pointer', fontFamily:'system-ui,sans-serif',
                      }}>{l}</button>
                    );
                  })}
                </div>
                {form.skirtLength === "Custom" && (
                  <textarea
                  style={{...S.inp, marginTop:8, minHeight:64, resize:'vertical',
                    fontStyle: focusedField === 'customSkirtLength' ? 'normal' : 'italic',
                    color: focusedField === 'customSkirtLength' ? '#1c1917' : '#a8a29e',
                    fontWeight: focusedField === 'customSkirtLength' ? 400 : 300,
                    transition:'font-style 0.1s, color 0.1s',
                  }}
                  placeholder="e.g., Tea-length, grazing mid-calf with a slight dip at back..."
                  value={form.customSkirtLength}
                  onChange={set('customSkirtLength')}
                  onFocus={() => setFocusedField('customSkirtLength')}
                  onBlur={() => setFocusedField(null)}
                />
                )}
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
                <div>
                  <label style={S.lbl}>Front Details</label>
                  <textarea style={{...S.inp, minHeight:72, resize:'vertical', fontStyle: focusedField === 'skirtFront' ? 'normal' : 'italic', color: focusedField === 'skirtFront' ? '#1c1917' : '#a8a29e', fontWeight: focusedField === 'skirtFront' ? 400 : 300, transition:'font-style 0.1s, color 0.1s'}} value={form.skirtFront} onChange={set('skirtFront')} onFocus={() => setFocusedField('skirtFront')} onBlur={() => setFocusedField(null)} />
                </div>
                <div>
                  <label style={S.lbl}>Back Details</label>
                  <textarea style={{...S.inp, minHeight:72, resize:'vertical', fontStyle: focusedField === 'skirtBack' ? 'normal' : 'italic', color: focusedField === 'skirtBack' ? '#1c1917' : '#a8a29e', fontWeight: focusedField === 'skirtBack' ? 400 : 300, transition:'font-style 0.1s, color 0.1s'}} value={form.skirtBack} onChange={set('skirtBack')} onFocus={() => setFocusedField('skirtBack')} onBlur={() => setFocusedField(null)} />
                </div>
              </div>
            </div>

          ) : (
            /* ── DRESS LAYOUT (default) ── */
            <div>
              <div style={{marginBottom:18}}>
                <label style={S.lbl}>Silhouette</label>
                <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6}}>
                  {SILHOUETTES.map(s => {
                    const on = form.cut === s;
                    return (
                      <button key={s} onClick={() => setForm(p => ({...p, cut:s}))} style={{
                        margin:0, padding:'9px 4px', borderRadius:10,
                        border:`1px solid ${on ? '#C9A0A0' : '#e7e5e4'}`,
                        backgroundColor: on ? '#C9A0A0' : '#ffffff',
                        color: on ? '#2D2926' : '#57534e',
                        fontWeight: on ? 700 : 600,
                        fontSize:11, cursor:'pointer', fontFamily:'system-ui,sans-serif',
                      }}>{s}</button>
                    );
                  })}
                </div>
                {form.cut==="Custom" && (
                  <textarea
                  style={{...S.inp, marginTop:8, minHeight:64, resize:'vertical',
                    fontStyle: focusedField === 'customCut' ? 'normal' : 'italic',
                    color: focusedField === 'customCut' ? '#1c1917' : '#a8a29e',
                    fontWeight: focusedField === 'customCut' ? 400 : 300,
                    transition:'font-style 0.1s, color 0.1s',
                  }}
                  placeholder="e.g., Dramatic Victorian bustle with cascading tiers..."
                  value={form.customCut}
                  onChange={set('customCut')}
                  onFocus={() => setFocusedField('customCut')}
                  onBlur={() => setFocusedField(null)}
                />
                )}
              </div>
              <div style={{marginBottom:18}}>
                <label style={S.lbl}>Dress Length</label>
                <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6}}>
                  {DRESS_LENGTHS.map(l => {
                    const on = form.dressLength === l;
                    return (
                      <button key={l} onClick={() => setForm(p => ({...p, dressLength:l}))} style={{
                        margin:0, padding:'9px 4px', borderRadius:10,
                        border:`1px solid ${on ? '#C9A0A0' : '#e7e5e4'}`,
                        backgroundColor: on ? '#C9A0A0' : '#ffffff',
                        color: on ? '#2D2926' : '#57534e',
                        fontWeight: on ? 700 : 600,
                        fontSize:11, cursor:'pointer', fontFamily:'system-ui,sans-serif',
                      }}>{l}</button>
                    );
                  })}
                </div>
                {form.dressLength === "Custom" && (
                  <textarea
                  style={{...S.inp, marginTop:8, minHeight:64, resize:'vertical',
                    fontStyle: focusedField === 'customDressLength' ? 'normal' : 'italic',
                    color: focusedField === 'customDressLength' ? '#1c1917' : '#a8a29e',
                    fontWeight: focusedField === 'customDressLength' ? 400 : 300,
                    transition:'font-style 0.1s, color 0.1s',
                  }}
                  placeholder="e.g., Floor-length with 6-inch puddle at back..."
                  value={form.customDressLength}
                  onChange={set('customDressLength')}
                  onFocus={() => setFocusedField('customDressLength')}
                  onBlur={() => setFocusedField(null)}
                />
                )}
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
                <SelectField field="neckline" lib={NECKLINE_LIBRARY} customField="customNeckline" ph="e.g., Deep plunge with fine-point V and crystal-edged trim..." label="Front Neckline" form={form} set={set} focusedField={focusedField} setFocusedField={setFocusedField} />
                <SelectField field="backNeckline" lib={BACK_NECKLINE_LIBRARY} customField="customBackNeckline" ph="e.g., Low open back with thin self-fabric strap across..." label="Back Neckline" form={form} set={set} focusedField={focusedField} setFocusedField={setFocusedField} />
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginTop:14}}>
                <SelectField field="sleeves"  lib={SLEEVE_LIBRARY}   customField="customSleeves"  ph="e.g., Sheer organza with gathered shoulder and tapered cuff..." label="Sleeves"  form={form} set={set} focusedField={focusedField} setFocusedField={setFocusedField} />
                <SelectField field="closure" lib={CLOSURE_LIBRARY} customField="customClosure" ph="e.g., Antique brass hook and bar closure at centre back..." label="Closure" form={form} set={set} focusedField={focusedField} setFocusedField={setFocusedField} />
              </div>
            </div>
          )}
        </div>

        {/* ── Color & Material ── */}
        <div style={S.card}>
          <div style={S.secHead}><Palette style={{width:13,height:13}} />Color & Material</div>

          {/* Colour palette — dropdown per family */}
          <div style={{marginBottom:18}}>
            <label style={S.lbl}>Colour</label>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10}}>
              {COLOUR_PALETTE.map(function(group) {
                const groupSelected = group.colours.find(function(c) { return c.name === (form.colorDescription || ''); });
                return (
                  <div key={group.group} style={{position:'relative'}}>
                    <div style={{
                      display:'flex', alignItems:'center', gap:8,
                      padding:'8px 32px 8px 10px',
                      backgroundColor:'#fff', borderRadius:10,
                      border: groupSelected ? '1px solid #C9A0A0' : '1px solid #e6dce2',
                      cursor:'pointer', position:'relative',
                    }}>
                      {groupSelected && (
                        <div style={{width:16,height:16,borderRadius:4,backgroundColor:groupSelected.hex,border:'1px solid #e0d9d3',flexShrink:0}}/>
                      )}
                      <select
                        value={groupSelected ? form.colorDescription : ''}
                        onChange={function(e) {
                          const val = e.target.value;
                          if (!val) return;
                          const colour = group.colours.find(function(c) { return c.name === val; });
                          if (colour) setForm(function(p) { return {...p, colorDescription:colour.name, colorHex:colour.hex}; });
                        }}
                        style={{
                          position:'absolute', inset:0, opacity:0, cursor:'pointer', width:'100%', height:'100%',
                        }}
                      >
                        <option value="">{group.group}</option>
                        {group.colours.map(function(colour) {
                          return <option key={colour.name} value={colour.name}>{colour.name}</option>;
                        })}
                      </select>
                      <span style={{
                        fontSize:10, fontWeight: groupSelected ? 700 : 500,
                        color: groupSelected ? '#2D2926' : '#a8a29e',
                        fontFamily:'system-ui,sans-serif', flex:1,
                        pointerEvents:'none',
                      }}>
                        {groupSelected ? groupSelected.name : group.group}
                      </span>
                      <ChevronDown style={{width:11,height:11,color:'#a8a29e',position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
          <div>
            <label style={S.lbl}>Textile</label>
            <div style={{position:'relative'}}>
              <select value={form.fabricId} onChange={set('fabricId')} style={S.sel}>
                {FABRIC_GROUPS.map(g => (
                  <optgroup key={g.group} label={g.group}>
                    {g.fabrics.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </optgroup>
                ))}
              </select>
              <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
            </div>
            {/* Fabric editorial description */}
            {(() => {
              const fab = FABRIC_LIBRARY.find(f => f.id === form.fabricId);
              if (!fab || !fab.editorial) return null;
              return (
                <div style={{marginTop:8, padding:'10px 14px', backgroundColor:'#f5f5f4',
                  borderRadius:10, border:'1px solid #f0eeec'}}>
                  <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
                    <span style={{fontSize:9, fontWeight:700, letterSpacing:'0.15em',
                      color:'#a8a29e', textTransform:'uppercase',
                      fontFamily:'system-ui,sans-serif'}}>
                      {fab.drape}
                    </span>
                    {fab.sustainable && (
                      <>
                        <span style={{color:'#ddd9d5', fontSize:10}}>·</span>
                        <span style={{fontSize:9, fontWeight:700, letterSpacing:'0.12em',
                          color:'#C9A0A0', textTransform:'uppercase',
                          fontFamily:'system-ui,sans-serif'}}>
                          ↻ Sustainable
                        </span>
                      </>
                    )}
                  </div>
                  <p style={{margin:0, fontSize:11, color:'#78716c', lineHeight:1.65,
                    fontStyle:'italic', fontWeight:300,
                    fontFamily:'system-ui,sans-serif'}}>
                    {fab.editorial}
                  </p>
                </div>
              );
            })()}
            {form.fabricId==="custom" && (
              <textarea
                  style={{...S.inp, marginTop:8, minHeight:64, resize:'vertical',
                    fontStyle: focusedField === 'customFabric' ? 'normal' : 'italic',
                    color: focusedField === 'customFabric' ? '#1c1917' : '#a8a29e',
                    fontWeight: focusedField === 'customFabric' ? 400 : 300,
                    transition:'font-style 0.1s, color 0.1s',
                  }}
                  placeholder="e.g., Organic hemp woven with mulberry silk — matte finish..."
                  value={form.customFabric}
                  onChange={set('customFabric')}
                  onFocus={() => setFocusedField('customFabric')}
                  onBlur={() => setFocusedField(null)}
                />
            )}
          </div>
        </div>

        {/* ── Lining ── */}
        <div style={S.card}>
          <div style={{...S.secHead, justifyContent:'space-between'}}>
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              <Layers2 style={{width:13,height:13}} />Lining
            </div>
          </div>
          <div>
            <label style={S.lbl}>Construction</label>
            <div style={{position:'relative'}}>
              <select value={form.lining} onChange={set('lining')} style={S.sel}>
                {['Fully Lined','Partially Lined (bodice only)','Self-Lined','Unlined',"To Maker's Discretion"].map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
              <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
            </div>
            <div style={{marginTop:8, padding:'10px 14px', backgroundColor:'#f5f5f4', borderRadius:10, border:'1px solid #f0eeec'}}>
              <p style={{margin:0, fontSize:11, color:'#78716c', lineHeight:1.65, fontStyle:'italic', fontWeight:300, fontFamily:'system-ui,sans-serif'}}>
                {form.lining === 'Fully Lined' && 'A full lining finishes the interior cleanly and adds structure. Standard for formal and bridal work.'}
                {form.lining === 'Partially Lined (bodice only)' && 'Bodice lined for comfort and structure; skirt unlined for drape and lightness. Common in chiffon and georgette designs.'}
                {form.lining === 'Self-Lined' && 'The outer fabric is used as its own lining — typical in heavier wovens and some structured silhouettes.'}
                {form.lining === 'Unlined' && 'No lining. Requires clean interior finishing — French seams, bound edges, or serging. Intentional and modern.'}
                {form.lining === "To Maker's Discretion" && 'Lining approach left to the maker based on fabric behaviour and construction. Note any preferences in the front/back detail fields.'}
              </p>
            </div>
          </div>
        </div>

        {/* ── Atmosphere & Context ── */}
        <div style={S.card}>
          <div style={{...S.secHead}}>
            <Wind style={{width:13,height:13}} />Setting
          </div>

          {/* Setting — curated radio buttons */}
          <div style={{marginBottom:20}}>
            <label style={S.lbl}>Setting</label>
            <p style={{margin:'0 0 12px',fontSize:11,color:'#a8a29e',fontStyle:'italic',fontFamily:'system-ui,sans-serif'}}>
              </p>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {CURATED_SETTINGS.map(function(setting) {
                const active = selectedSetting === setting.id;
                return (
                  <button
                    key={setting.id}
                    onClick={function() {
                      setSelectedSetting(setting.id);
                      setForm(function(p) { return {...p, atmosphere: setting.description}; });
                    }}
                    style={{
                      display:'flex', alignItems:'flex-start', gap:12, padding:'10px 14px',
                      borderRadius:12, textAlign:'left', cursor:'pointer',
                      border: active ? '1px solid #C9A0A0' : '1px solid #e7e5e4',
                      backgroundColor: active ? '#fdf3f3' : '#fff',
                      transition:'all 0.15s ease',
                    }}
                  >
                    <div style={{
                      width:16, height:16, borderRadius:'50%', flexShrink:0, marginTop:1,
                      border: active ? '5px solid #C9A0A0' : '1.5px solid #d0c8cc',
                      backgroundColor:'#fff', transition:'all 0.15s ease',
                    }} />
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:'#2D2926',fontFamily:'system-ui,sans-serif',marginBottom:2}}>
                        {setting.label}
                      </div>
                      <div style={{fontSize:10,color:'#a8a29e',fontFamily:'system-ui,sans-serif',lineHeight:1.5}}>
                        {setting.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{marginBottom:20}}>
            <div style={{...S.secHead, marginBottom:14}}>
              <Scissors style={{width:13,height:13}} />Design Details
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>
                <label style={S.lbl}>Bodice Construction</label>
                <div style={{position:'relative'}}>
                  <select value={form.designDetails.bodice} onChange={setDesignDetail('bodice')} style={S.sel}>
                    {DESIGN_DETAIL_OPTIONS.bodice.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                  <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
                </div>
              </div>
              <div>
                <label style={S.lbl}>Waist Definition</label>
                <div style={{position:'relative'}}>
                  <select value={form.designDetails.waist} onChange={setDesignDetail('waist')} style={S.sel}>
                    {DESIGN_DETAIL_OPTIONS.waist.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                  <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
                </div>
              </div>
              <div>
                <label style={S.lbl}>Skirt Behavior</label>
                <div style={{position:'relative'}}>
                  <select value={form.designDetails.skirtBehavior} onChange={setDesignDetail('skirtBehavior')} style={S.sel}>
                    {DESIGN_DETAIL_OPTIONS.skirtBehavior.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                  <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
                </div>
              </div>
              <div>
                <label style={S.lbl}>Skirt Features</label>
                <div style={{position:'relative'}}>
                  <select value={form.designDetails.skirtFeature} onChange={setDesignDetail('skirtFeature')} style={S.sel}>
                    {DESIGN_DETAIL_OPTIONS.skirtFeature.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                  <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
                </div>
              </div>
              <div>
                <label style={S.lbl}>Surface Treatment</label>
                <div style={{position:'relative'}}>
                  <select value={form.designDetails.surface} onChange={setDesignDetail('surface')} style={S.sel}>
                    {DESIGN_DETAIL_OPTIONS.surface.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                  <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
                </div>
              </div>
              <div>
                <label style={S.lbl}>Train</label>
                <div style={{position:'relative'}}>
                  <select value={form.designDetails.hem} onChange={setDesignDetail('hem')} style={S.sel}>
                    {DESIGN_DETAIL_OPTIONS.hem.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                  <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#a8a29e',pointerEvents:'none'}} />
                </div>
              </div>
            </div>

            <div style={{marginTop:12,padding:'12px 14px',backgroundColor:'#faf8f6',border:'1px solid #eee7e3',borderRadius:12}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',color:'#7c6f74',marginBottom:8}}>
                Locked Summary
              </div>
              <div style={{fontSize:11,color:'#2D2926',lineHeight:1.6,fontFamily:'system-ui,sans-serif'}}>
                {form.designDetailsSummary}
              </div>
            </div>

            {/* ── Reference image upload ── */}
            <div style={{marginTop:16,marginBottom:16,padding:'14px 16px',backgroundColor:'#faf8f7',borderRadius:12,border:'1px solid #ede8e3'}}>
              <label style={{...S.lbl, marginBottom:6}}>
                Start with a reference photo{' '}
                <span style={{fontWeight:400,color:'#a8a29e',fontSize:10,textTransform:'none',letterSpacing:0,fontStyle:'italic'}}>(Optional)</span>
              </label>

              {!referenceImageBase64 ? (
                <div>
                  <label htmlFor="refImageInput" style={{
                    display:'flex', alignItems:'center', gap:10,
                    padding:'10px 14px', borderRadius:10,
                    border:'1.5px dashed #d4c9ce', cursor:'pointer',
                    backgroundColor:'#fff', transition:'border-color 0.2s',
                  }}>
                    <Eye style={{width:14,height:14,color:'#C9A0A0',flexShrink:0}}/>
                    <span style={{fontSize:11,color:'#78716c',fontFamily:'system-ui,sans-serif'}}>
                      Upload a dress photo to inspire your design
                    </span>
                  </label>
                  <input
                    id="refImageInput"
                    type="file"
                    accept="image/*"
                    style={{display:'none'}}
                    onChange={async function(e) {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = async function(ev) {
                        const dataUrl = ev.target.result;
                        const mime = dataUrl.split(',')[0].match(/:(.*?);/)[1];
                        const b64  = dataUrl.split(',')[1];
                        setReferenceImageBase64(b64);
                        setReferenceImageMime(mime);
                        setReferenceImageName(file.name);
                        setImageInterpretationNote('');
                        setImageInterpretationError('');
                        setIsInterpretingImage(true);
                        try {
                          const result = await interpretReferenceImage(b64, mime, form);
                          // Only apply the detail description — never touch structural selections
                          if (result.details) {
                            setForm(function(prev) {
                              return {
                                ...prev,
                                originalComments: result.details,
                                aiInterpretedComments: '',
                              };
                            });
                          }
                          setImageInterpretationNote(result.notes || 'Details read from your photo — review and adjust in the field below.');
                        } catch (err) {
                          console.error('Image interpretation failed:', err);
                          setImageInterpretationError('Could not read the image — please describe your design in the notes field below.');
                        } finally {
                          setIsInterpretingImage(false);
                        }
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <p style={{margin:'8px 0 0',fontSize:10,color:'#a8a29e',fontStyle:'italic',fontFamily:'system-ui,sans-serif',lineHeight:1.6}}>
                    Your photo is used only to interpret the design — the image generator is restricted from reproducing or replicating photos.
                  </p>
                </div>
              ) : (
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                    <div style={{
                      width:48,height:48,borderRadius:8,overflow:'hidden',flexShrink:0,
                      backgroundImage:`url(data:${referenceImageMime};base64,${referenceImageBase64})`,
                      backgroundSize:'cover',backgroundPosition:'center top',
                      border:'1px solid #e7e5e4',
                    }}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#2D2926',fontFamily:'system-ui,sans-serif',marginBottom:2}}>
                        {referenceImageName}
                      </div>
                      {isInterpretingImage ? (
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <Loader2 style={{width:11,height:11,color:'#C9A0A0',animation:'spin 1s linear infinite'}}/>
                          <span style={{fontSize:10,color:'#C9A0A0',fontFamily:'system-ui,sans-serif'}}>Reading your design…</span>
                        </div>
                      ) : imageInterpretationNote ? (
                        <div style={{fontSize:10,color:'#2a6e42',fontFamily:'system-ui,sans-serif',lineHeight:1.5}}>
                          ✓ {imageInterpretationNote}
                        </div>
                      ) : imageInterpretationError ? (
                        <div style={{fontSize:10,color:'#c8963c',fontFamily:'system-ui,sans-serif',lineHeight:1.5}}>
                          {imageInterpretationError}
                        </div>
                      ) : null}
                    </div>
                    <button
                      onClick={function() {
                        setReferenceImageBase64(null);
                        setReferenceImageMime(null);
                        setReferenceImageName('');
                        setImageInterpretationNote('');
                        setImageInterpretationError('');
                        document.getElementById('refImageInput') && (document.getElementById('refImageInput').value = '');
                      }}
                      style={{padding:'4px 10px',borderRadius:8,border:'1px solid #e7e5e4',backgroundColor:'#fff',fontSize:10,color:'#78716c',cursor:'pointer',fontFamily:'system-ui,sans-serif',flexShrink:0}}
                    >
                      Remove
                    </button>
                  </div>
                  <p style={{margin:0,fontSize:10,color:'#a8a29e',fontStyle:'italic',fontFamily:'system-ui,sans-serif',lineHeight:1.6}}>
                    Your photo is used only to interpret the design — the image generator is restricted from reproducing or replicating photos.
                  </p>
                </div>
              )}
            </div>

            <div style={{marginTop:16,marginBottom:6}}>
              <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',marginBottom:6}}>
                <label style={S.lbl}>Bring My Ideas to Life <span style={{fontWeight:400,color:'#a8a29e',fontSize:10,textTransform:'none',letterSpacing:0,fontStyle:'italic'}}>(Optional)</span></label>
                <span style={{fontSize:9,color: commentValue.length > 160 ? (commentValue.length >= 200 ? '#ef4444' : '#c8963c') : '#c4bfba', fontFamily:'system-ui,sans-serif', letterSpacing:'0.05em'}}>
                  {commentValue.length}/400
                </span>
              </div>

            </div>
            <textarea
              value={commentValue}
              maxLength={400}
              onChange={function(e) {
                setOriginalComments(e.target.value);
                setRefinedSuggestion(null);
                setInterpreterQuestions([]);
              }}
              placeholder="Describe a detail, a feeling, or something specific — beading on the bodice, a draped back, pockets."
              style={{
                ...S.inp, minHeight:92, resize:'vertical',
                fontStyle: commentValue ? 'normal' : 'italic',
                color: commentValue ? '#1c1917' : '#a8a29e',
                fontWeight: commentValue ? 400 : 300,
                lineHeight: 1.5,
              }}
            />

            <div style={{marginTop:10,padding:'12px 14px',backgroundColor:'#faf8f6',border:'1px solid #eee7e3',borderRadius:12}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:12,flexWrap:'wrap'}}>
                {!referenceImageBase64 && (
                <button
                  onClick={refineMyIdea}
                  disabled={isInterpreting || !String(commentValue || '').trim()}
                  style={{
                    display:'inline-flex',alignItems:'center',justifyContent:'center',gap:8,
                    padding:'10px 14px',borderRadius:999,border:'1px solid #d9c9c9',
                    backgroundColor:isInterpreting ? '#efe7e7' : '#fff',color:'#2D2926',
                    fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',
                    cursor:(isInterpreting || !String(commentValue || '').trim()) ? 'not-allowed' : 'pointer',
                    fontFamily:'system-ui,sans-serif'
                  }}
                >
                  {isInterpreting ? <Loader2 style={{width:14,height:14,animation:'spin 1s linear infinite'}} /> : <Sparkles style={{width:14,height:14}} />}
                  {isInterpreting ? 'Refining...' : 'Refine My Ideas'}
                </button>
                )}
              </div>

              {appliedCommentValue && !refinedSuggestion && (
                <div style={{marginTop:12,padding:'12px 14px',backgroundColor:'#fff',border:'1px solid #eadfdf',borderRadius:10}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',color:'#7c6f74',marginBottom:8}}>
                    Applied interpretation
                  </div>
                  <div style={{fontSize:11,color:'#2D2926',lineHeight:1.6,fontFamily:'system-ui,sans-serif'}}>
                    {appliedCommentValue}
                  </div>
                </div>
              )}

              {refinedSuggestion && (
                <div style={{marginTop:12,padding:'12px 14px',backgroundColor:'#fff',border:'1px solid #eadfdf',borderRadius:10}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',color:'#7c6f74',marginBottom:8}}>
                    Suggested interpretation
                  </div>
                  <div style={{fontSize:11,color:'#2D2926',lineHeight:1.6,fontFamily:'system-ui,sans-serif'}}>
                    {refinedSuggestion.refinedDetails}
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:12}}>
                    <button
                      onClick={applyRefinedIdea}
                      style={{
                        padding:'10px 14px',borderRadius:999,border:'none',backgroundColor:'#C9A0A0',color:'#2D2926',
                        fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',cursor:'pointer',fontFamily:'system-ui,sans-serif'
                      }}
                    >
                      Apply Interpretation
                    </button>
                    <button
                      onClick={function() { setRefinedSuggestion(null); setInterpreterQuestions([]); }}
                      style={{
                        padding:'10px 14px',borderRadius:999,border:'1px solid #e7dede',backgroundColor:'#fff',color:'#57534e',
                        fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',cursor:'pointer',fontFamily:'system-ui,sans-serif'
                      }}
                    >
                      Keep Original
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {form.separates && <div style={{marginBottom:22}} />}

          {/* Generate button */}
          <button onClick={generate} disabled={isGenerating} style={{
            display:'flex', alignItems:'center', justifyContent:'center', gap:10,
            margin:0, width:'100%', padding:'18px 0',
            backgroundColor: isGenerating ? '#a8887f' : '#C9A0A0',
            color:isGenerating ? '#f5f4f2' : '#2D2926', fontWeight:700, fontSize:11, letterSpacing:'0.35em', textTransform:'uppercase',
            borderRadius:14, border:'none', cursor: isGenerating ? 'not-allowed' : 'pointer',
            boxShadow:'0 4px 20px rgba(45,41,38,0.22)', fontFamily:'system-ui,sans-serif',
          }}>
            {isGenerating
              ? <Loader2 style={{width:17,height:17,animation:'spin 1s linear infinite'}} />
              : <Sparkles style={{width:17,height:17}} />
            }
            {isGenerating ? 'Generating...' : 'Generate Design'}
          </button>

          {/* AI generation note */}
          <div style={{display:'flex',gap:8,alignItems:'flex-start',marginTop:10,padding:'10px 14px',backgroundColor:'#fafaf9',borderRadius:10,border:'1px solid #f0eeec'}}>
            <Info style={{width:12,height:12,color:'#c4bfba',flexShrink:0,marginTop:1}}/>
            <p style={{margin:0,fontSize:10,color:'#a8a29e',lineHeight:1.6,fontStyle:'italic',fontFamily:'system-ui,sans-serif'}}>
              AI image generation is an emerging technology. The translation from text to image is still imperfect — please be patient with the results and edit as needed.
            </p>
          </div>

          {error && <p style={{margin:0,padding:0,marginTop:10,color:'#ef4444',fontSize:10,fontWeight:700,textAlign:'center',letterSpacing:'0.1em',textTransform:'uppercase'}}>{error}</p>}

          {/* ── Load by Design No. ── only shown when no design is active */}
          {!activeDesign && !isGenerating && (
            <div style={{marginTop:10,padding:'12px 14px',backgroundColor:'#f9f7f6',borderRadius:10,border:'1px solid #e7e5e4'}}>
              <p style={{margin:'0 0 8px',fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',color:'#78716c',fontFamily:'system-ui,sans-serif'}}>
                Load a Design No.
              </p>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 571950"
                  value={seedSearchValue}
                  onChange={function(e) {
                    setSeedSearchValue(e.target.value.replace(/[^0-9]/g, ''));
                    setSeedSearchError('');
                  }}
                  onKeyDown={function(e) {
                    if (e.key === 'Enter') e.target.nextSibling?.click();
                  }}
                  style={{
                    flex:1, padding:'9px 12px', borderRadius:9,
                    border:'1px solid #e0d9d3', fontSize:11, fontFamily:'system-ui,sans-serif',
                    color:'#2D2926', outline:'none', backgroundColor:'#fff',
                  }}
                />
                <button
                  onClick={async function() {
                    const val = seedSearchValue.trim();
                    if (!val) { setSeedSearchError('Enter a Design No.'); return; }
                    const num = parseInt(val, 10);
                    if (isNaN(num) || num < 1) { setSeedSearchError('Invalid Design No.'); return; }
                    setSeedSearchLoading(true);
                    setSeedSearchError('');
                    try {
                      const found = await onLoadBySeed(num);
                      if (found === false) {
                        setSeedSearchError('Design No. ' + num + ' not found.');
                      } else {
                        setSeedSearchValue('');
                      }
                    } catch (e) {
                      setSeedSearchError('Search failed — please try again.');
                    } finally {
                      setSeedSearchLoading(false);
                    }
                  }}
                  style={{
                    padding:'9px 16px', borderRadius:9, border:'none',
                    backgroundColor: seedSearchLoading ? '#a8a29e' : '#2D2926', color:'#fff',
                    fontSize:10, fontWeight:700, letterSpacing:'0.12em',
                    textTransform:'uppercase', cursor: seedSearchLoading ? 'default' : 'pointer',
                    fontFamily:'system-ui,sans-serif', whiteSpace:'nowrap',
                    transition:'background-color 0.15s',
                  }}
                  disabled={seedSearchLoading}
                >
                  {seedSearchLoading ? 'Searching…' : 'Load'}
                </button>
              </div>
              {seedSearchError && (
                <p style={{margin:'6px 0 0',fontSize:10,color:'#c8963c',fontFamily:'system-ui,sans-serif'}}>
                  {seedSearchError}
                </p>
              )}
              <p style={{margin:'6px 0 0',fontSize:10,color:'#a8a29e',fontStyle:'italic',fontFamily:'system-ui,sans-serif',lineHeight:1.5}}>
                Enter a Design No. from a saved design or shared image to recreate it.
              </p>
            </div>
          )}

          {/* Design No. display — shown when seed exists */}
          {designSeed && (
            <div style={{marginTop:12,padding:'10px 14px',backgroundColor: isSaved ? '#f0faf2' : '#f5f5f4',borderRadius:10,border:`1px solid ${isSaved ? '#b8dfc4' : '#e7e5e4'}`}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  {isSaved
                    ? <span style={{fontSize:12,lineHeight:1}}>✓</span>
                    : <Lock style={{width:12,height:12,color:'#78716c'}}/>
                  }
                  <span style={{fontSize:10,fontWeight:600,color: isSaved ? '#2a6e42' : '#57534e',fontFamily:'system-ui,sans-serif'}}>
                    {isSaved
                      ? `Saved to your collection — Tabitha — Design No. ${designSeed}`
                      : `Tabitha — Design No. ${designSeed} — not yet saved`}
                  </span>
                </div>
                {isSaved && activeDesign && (
                  <button
                    onClick={function() { setIsLocked(false); setDesignSeed(null); setIsSaved(false); }}
                    style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:999,backgroundColor:'#C9A0A0',border:'none',cursor:'pointer',fontSize:9,fontWeight:700,color:'#2D2926',letterSpacing:'0.1em',textTransform:'uppercase',fontFamily:'system-ui,sans-serif',boxShadow:'0 2px 8px rgba(201,160,160,0.3)'}}
                  >
                    <Sparkles style={{width:10,height:10}}/>
                    Start New Version
                  </button>
                )}
              </div>
            </div>
          )}



          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginTop:10}}>
            <button
              disabled={isSaved}
              onClick={function() {
                if (!isSignedIn) {
                  setShowSignInGate(true);
                  return;
                }
                onSaveToCollection({
                  form,
                  designSpec: buildCanonicalDesignSpec(form),
                  designRender: activeDesign?.front || null,
                  designRenderBack: null,
                  designSeed,
                  isLocked,
                });
                setIsSaved(true);
                setSavedToast(true);
                setTimeout(function() { setSavedToast(false); }, 3000);
              }}
              style={{
                padding:'12px 0',borderRadius:12,
                border: (!isSaved && activeDesign) ? 'none' : '1px solid #C9A0A0',
                backgroundColor: (!isSaved && activeDesign) ? '#C9A0A0' : '#fdf3f3',
                color: isSaved ? '#a8a29e' : '#2D2926',
                fontSize:10, fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase',
                cursor: isSaved ? 'default' : 'pointer',
                fontFamily:'system-ui,sans-serif',
                opacity: isSaved ? 0.7 : 1,
              }}
            >
              {savedToast ? '✓ Saved!' : isSaved ? 'Saved to Collection' : 'Save to My Collection'}
            </button>
            <button
              onClick={() => onProceedToFittingRoom({
                form,
                designSpec: buildCanonicalDesignSpec(form),
                designRender: activeDesign?.front || null,
                designRenderBack: null,
                designSeed: designSeed,
                isLocked: isLocked,
              })}
              disabled={isGenerating}
              style={{
                padding:'12px 0',borderRadius:12,border:'none',
                backgroundColor: isGenerating ? '#e7e5e4' : C,
                color: isGenerating ? '#b8b0a8' : '#fff',
                fontSize:10,fontWeight:700,letterSpacing:'0.2em',textTransform:'uppercase',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                fontFamily:'system-ui,sans-serif',
              }}
            >
              Proceed to The Fitting Room
            </button>
          </div>

          {/* ── Submit to Gallery — only shown once design is saved ── */}
          {isSaved && activeDesign && (
            <button
              disabled={gallerySubmitState === 'submitting' || gallerySubmitState === 'done'}
              onClick={async function() {
                if (gallerySubmitState !== 'idle') return;
                setGallerySubmitState('submitting');
                try {
                  await onSubmitToGallery({
                    form,
                    designRender: activeDesign?.front || null,
                    designSeed,
                  });
                  setGallerySubmitState('done');
                } catch (err) {
                  console.error('Gallery submit failed:', err);
                  setGallerySubmitState('error');
                  setTimeout(function() { setGallerySubmitState('idle'); }, 4000);
                }
              }}
              style={{
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                width:'100%', marginTop:8, padding:'11px 0', borderRadius:12,
                border: gallerySubmitState === 'done' ? '1px solid #b8dfc4' : '1px solid #e6dce2',
                backgroundColor: gallerySubmitState === 'done' ? '#f0faf2' : gallerySubmitState === 'error' ? '#fdf3f3' : '#fdf8f8',
                color: gallerySubmitState === 'done' ? '#2a6e42' : gallerySubmitState === 'error' ? '#b04040' : '#78716c',
                fontSize:10, fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase',
                cursor: (gallerySubmitState === 'submitting' || gallerySubmitState === 'done') ? 'default' : 'pointer',
                fontFamily:'system-ui,sans-serif',
                transition:'all 0.2s ease',
              }}
            >
              {gallerySubmitState === 'submitting' && <Loader2 style={{width:11,height:11,animation:'spin 1s linear infinite'}} />}
              {gallerySubmitState === 'done'       && <span style={{fontSize:12}}>✓</span>}
              {gallerySubmitState === 'idle'       && <Sparkles style={{width:11,height:11,color:'#C9A0A0'}} />}
              {gallerySubmitState === 'idle'       ? 'Submit to Gallery'
                : gallerySubmitState === 'submitting' ? 'Submitting…'
                : gallerySubmitState === 'done'       ? 'Submitted — under review'
                : 'Submission failed — try again'}
            </button>
          )}
        </div>

        {/* ── Preview panel ── */}
        <div style={{...S.card, padding:0, overflow:'hidden'}}>
          <div style={{backgroundColor:'#1a1714', position:'relative'}}>
            {activeDesign ? (
              <div style={{width:'100%', position:'relative'}}>
                <img src={activeDesign.front} alt="Front View" style={{display:'block', width:'100%', height:'auto'}} />
                <div style={{position:'absolute', top:10, left:10, backgroundColor:'rgba(26,23,20,0.62)', backdropFilter:'blur(6px)', padding:'4px 12px', borderRadius:999, fontSize:8, fontWeight:700, letterSpacing:'0.12em', color:'rgba(255,255,255,0.92)', fontFamily:'system-ui,sans-serif', pointerEvents:'none'}}>
                  Tabitha{designSeed ? ` — Design No. ${designSeed}` : ''}
                </div>

                {/* Share buttons row — bottom left */}
                <div style={{ position:'absolute', bottom:10, left:10, display:'flex', gap:6 }}>
                  {/* Instagram */}
                  <button
                    onClick={function() {
                      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                      window.open(isMobile ? "instagram://" : "https://www.instagram.com", "_blank");
                    }}
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:999, border:'none', backgroundColor:'rgba(255,255,255,0.92)', cursor:'pointer', boxShadow:'0 2px 8px rgba(0,0,0,0.12)' }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#2D2926" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                    <span style={{fontSize:9, fontWeight:700, letterSpacing:'0.1em', color:'#2D2926', fontFamily:'system-ui,sans-serif', textTransform:'uppercase'}}>Instagram</span>
                  </button>
                  {/* Pinterest */}
                  <button
                    onClick={function() {
                      const url  = encodeURIComponent(window.location.href);
                      const desc = encodeURIComponent("I designed this dress with Tabitha Studio — bespoke dressmaking made accessible.");
                      window.open("https://pinterest.com/pin/create/button/?url=" + url + "&description=" + desc, "_blank");
                    }}
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:999, border:'none', backgroundColor:'rgba(255,255,255,0.92)', cursor:'pointer', boxShadow:'0 2px 8px rgba(0,0,0,0.12)' }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="#2D2926"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
                    <span style={{fontSize:9, fontWeight:700, letterSpacing:'0.1em', color:'#2D2926', fontFamily:'system-ui,sans-serif', textTransform:'uppercase'}}>Pinterest</span>
                  </button>
                  {/* General share — composites watermark + design no. before sharing */}
                  <button
                    onClick={async function() {
                      const src = activeDesign?.front;
                      if (!src) return;
                      const filename = "Tabitha-Design-" + (designSeed || "render") + ".jpg";
                      const shareData = { title:"My Tabitha Studio Design — No. " + designSeed, text:"I designed this dress with Tabitha Studio.", url:window.location.href };
                      try {
                        const blob = await buildBrandedBlob(src);
                        const file = new File([blob], filename, { type:"image/jpeg" });
                        if (navigator.canShare && navigator.canShare({ files:[file] })) {
                          await navigator.share({ ...shareData, files:[file] });
                          return;
                        }
                        if (navigator.share) { try { await navigator.share(shareData); return; } catch (_) {} }
                        // Fallback: download the branded image
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a"); a.href = url; a.download = filename;
                        document.body.appendChild(a); a.click(); document.body.removeChild(a);
                        setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
                      } catch (_) {
                        if (navigator.share) { try { await navigator.share(shareData); return; } catch (__) {} }
                        const a = document.createElement("a"); a.href = src; a.download = filename; a.click();
                      }
                    }}
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:999, border:'none', backgroundColor:'rgba(45,41,38,0.85)', cursor:'pointer', boxShadow:'0 2px 8px rgba(0,0,0,0.12)' }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                    <span style={{fontSize:9, fontWeight:700, letterSpacing:'0.1em', color:'#fff', fontFamily:'system-ui,sans-serif', textTransform:'uppercase'}}>Share</span>
                  </button>
                  {/* Download — composites watermark + design no. via buildBrandedBlob */}
                  <button
                    onClick={async function() {
                      const src = activeDesign?.front;
                      if (!src) return;
                      const filename = "Tabitha-Design-" + (designSeed || "render") + ".jpg";
                      try {
                        const blob = await buildBrandedBlob(src);
                        const url  = URL.createObjectURL(blob);
                        const a    = document.createElement("a");
                        a.href     = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
                      } catch (_) {
                        // Canvas blocked — plain download fallback
                        const a = document.createElement("a");
                        a.href     = src;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }
                    }}
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:999, border:'none', backgroundColor:'rgba(45,41,38,0.85)', cursor:'pointer', boxShadow:'0 2px 8px rgba(0,0,0,0.12)' }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <span style={{fontSize:9, fontWeight:700, letterSpacing:'0.1em', color:'#fff', fontFamily:'system-ui,sans-serif', textTransform:'uppercase'}}>Download</span>
                  </button>
                </div>
                <button onClick={() => setActiveDesign(null)} style={{
                  position:'absolute', top:10, right:10, margin:0,
                  backgroundColor:'rgba(255,255,255,0.92)', border:'none',
                  borderRadius:'50%', width:34, height:34, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  boxShadow:'0 2px 8px rgba(0,0,0,0.12)',
                }}>
                  <RotateCcw style={{width:13,height:13,color:'#57534e'}} />
                </button>
              </div>
            ) : (
              <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:420, opacity:0.25}}>
                <Eye style={{width:44,height:44,marginBottom:10,color:'#fff'}} />
                <span style={{margin:0,padding:0,fontSize:10,fontWeight:700,letterSpacing:'0.3em',textTransform:'uppercase',color:'#fff'}}>Ready for Render</span>
              </div>
            )}
            {isGenerating && (
              <div style={{position:'absolute', inset:0, backgroundColor:'rgba(26,23,20,0.88)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10}}>
                <div style={{textAlign:'center'}}>
                  <Loader2 style={{width:30,height:30,color:'#C9A0A0',animation:'spin 1s linear infinite',margin:'0 auto 10px'}} />
                  <span style={{margin:0,padding:0,fontSize:10,fontWeight:700,letterSpacing:'0.2em',textTransform:'uppercase',color:'#fff'}}>Processing Design...</span>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── New Version modal — fires on first edit of a saved design ── */}
      {showVersionModal && (
        <div style={{
          position:'fixed', inset:0, zIndex:200,
          backgroundColor:'rgba(26,23,20,0.72)',
          backdropFilter:'blur(6px)',
          display:'flex', alignItems:'center', justifyContent:'center',
          padding:'24px 20px',
        }} onClick={function(e) { e.stopPropagation(); }}>
          <div style={{
            backgroundColor:'#fff', borderRadius:20,
            padding:'32px 28px', maxWidth:360, width:'100%',
            boxShadow:'0 30px 80px rgba(0,0,0,0.35)',
            textAlign:'center',
          }}>
            {/* Icon */}
            <div style={{
              width:48, height:48, borderRadius:'50%',
              backgroundColor:'#fdf3f3', border:'1px solid #e9dde4',
              display:'flex', alignItems:'center', justifyContent:'center',
              margin:'0 auto 18px',
            }}>
              <Sparkles style={{width:20, height:20, color:'#C9A0A0'}} />
            </div>

            <div style={{fontSize:15, fontWeight:700, color:'#2D2926', marginBottom:8, fontFamily:'system-ui,sans-serif', letterSpacing:'-0.01em'}}>
              Save a new version of this design?
            </div>
            <p style={{margin:'0 0 6px', fontSize:12, color:'#57534e', lineHeight:1.65, fontFamily:'system-ui,sans-serif'}}>
              Tabitha — Design No. {designSeed} is saved and safe.
            </p>
            <p style={{margin:'0 0 24px', fontSize:12, color:'#78716c', lineHeight:1.65, fontFamily:'system-ui,sans-serif'}}>
              To save your changes as a new design, hit <strong>Start New Version</strong> — then generate and save when you're ready.
            </p>

            <button
              onClick={function() {
                setIsLocked(false);
                setDesignSeed(null);
                setIsSaved(false);
                setShowVersionModal(false);
              }}
              style={{
                display:'block', width:'100%',
                padding:'14px 0', borderRadius:12, border:'none',
                backgroundColor:'#C9A0A0', color:'#2D2926',
                fontSize:10, fontWeight:700, letterSpacing:'0.25em', textTransform:'uppercase',
                cursor:'pointer', fontFamily:'system-ui,sans-serif',
                boxShadow:'0 4px 16px rgba(201,160,160,0.4)',
                marginBottom:10,
              }}
            >
              Yes — start new version
            </button>
            <button
              onClick={function() { setShowVersionModal(false); }}
              style={{
                display:'block', width:'100%',
                padding:'12px 0', borderRadius:12,
                border:'1px solid #e7e5e4', backgroundColor:'transparent',
                color:'#78716c', fontSize:10, fontWeight:700,
                letterSpacing:'0.2em', textTransform:'uppercase',
                cursor:'pointer', fontFamily:'system-ui,sans-serif',
              }}
            >
              No — keep this design
            </button>
          </div>
        </div>
      )}

      {/* ── Sign-in gate modal ── */}
      {showSignInGate && (
        <SignInGateModal
          onSignIn={onSignIn}
          onClose={function() { setShowSignInGate(false); }}
        />
      )}

    </div>
  );
}
